import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Card, Space, Button, Input, InputNumber, Typography, Table, Checkbox, Modal, message, Select, Tabs, Upload, Divider, Pagination, Tag } from 'antd';
import request from '../../../../utils/request';
import { HelpHintButton } from '../../../../components/HelpHintButton';
// 仅用于步骤4：附件列表按钮（此处不使用图标）

// 1) 产出容器放入仓位
type PutAwayOp = { container_code?: string; location_code?: string };
// 2) 无法操作产品：登记并生成容器放入仓位
type UnoperableOp = { unit_code?: string; qty?: number; reason?: string; new_container?: string; location_code?: string };
// 3) 多拣货产品：生成容器放入仓位
type OverPickOp = { unit_code?: string; qty?: number; new_container?: string; location_code?: string };

type Props = {
  taskId: number;
  stageNo: number;
  executionSessionId?: string | null;
  step?: any;
  // 通用渲染器签名兼容
  data?: any;
  metrics?: any;
  onChange?: (k: string, v: any) => void;
  instructionDetail?: any;
  isSortCountTemplate?: boolean;
  instructionCodeFromParent?: string;
  canOperate?: boolean;
  onSubmit?: (metrics: any) => Promise<void> | void;
  isBatch?: boolean;
  batchDetail?: any;
};

const { Text } = Typography;

type ExceptionAllocRow = {
  inventory_unit_id: number;
  unit_code: string;
  quantity: number;
  allocation_semantic?: string;
  aiu_conversion_pending?: boolean;
};
type ExceptionContainerRow = {
  code: string;
  total_qty: number;
  allocations: ExceptionAllocRow[];
  allocation_semantic?: string;
  finalize_conversion_semantic?: string;
  aiu_conversion_pending?: boolean;
  demand_mode?: string;
  exception_total_weight_g?: number;
  aiu_unit_code?: string;
  aiu_inventory_unit_id?: number;
};

type OverPickSortRow = {
  group?: string;
  old_code?: string;
  picked_qty?: number;
};

type OverPickGroup = {
  group_code: string; // sorting_group system_code
  group_id?: number;
  instruction_id?: number;
  temp_loc_no: number; // 临时分拣仓位号（稳定递增，不随数组下标变化）
  items: Array<{ old_code: string; qty: number; inventory_unit_code?: string; inventory_unit_id?: number }>;
  created_container_id?: number;
  created_container_code?: string;
};

const buildDraftKey = (taskId: number, stageNo: number, stepId?: number | string, executionSessionId?: string | null) => {
  const sessionRaw = String(executionSessionId || '').trim();
  const sessionPart = sessionRaw || 'NO_SESSION';
  return `WRAPUP_DRAFT_${taskId}_${stageNo}_${stepId ?? 'x'}_${sessionPart}`;
};

const SubmitWrapUpRenderer: React.FC<Props> = ({ taskId, stageNo, executionSessionId, step, data: dataProp, metrics: metricsProp, onChange, instructionDetail, isSortCountTemplate, instructionCodeFromParent, canOperate: canOperateProp, onSubmit, isBatch, batchDetail }) => {
  // 透传数据：优先使用 props.data，其次 step.data（用于拿到“产出容器列表”）
  const data = (dataProp != null ? dataProp : (step as any)?.data);
  const stepId = useMemo(() => Number(step?.id ?? step?.step_id ?? 0) || 0, [step]);
  const draftKey = useMemo(
    () => buildDraftKey(taskId, stageNo, stepId, executionSessionId),
    [taskId, stageNo, stepId, executionSessionId],
  );
  const [putAwayOps, setPutAwayOps] = useState<PutAwayOp[]>([]);
  const [unoperableOps, setUnoperableOps] = useState<UnoperableOp[]>([]);
  const [overPickOps, setOverPickOps] = useState<OverPickOp[]>([]);
  const [remarks, setRemarks] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [unoperableQty, setUnoperableQty] = useState<number>(0);
  const [need2ExceptionTotalWeightG, setNeed2ExceptionTotalWeightG] = useState<number>(0);
  const [unoperableCreating, setUnoperableCreating] = useState<boolean>(false);
  const [exceptionContainers, setExceptionContainers] = useState<ExceptionContainerRow[]>([]);
  const [allocModalOpen, setAllocModalOpen] = useState<boolean>(false);
  const [allocModalTitle, setAllocModalTitle] = useState<string>('');
  const [allocModalRows, setAllocModalRows] = useState<ExceptionAllocRow[]>([]);
  const [manualModalOpen, setManualModalOpen] = useState<boolean>(false);
  const [manualRemainder, setManualRemainder] = useState<number>(0);
  const [manualCandidates, setManualCandidates] = useState<Array<{ inventory_unit_id: number; unit_code: string; gap: number; content_lines?: string[] }>>([]);
  const [manualInputs, setManualInputs] = useState<Record<string, number>>({});
  const [manualSearchKeyword, setManualSearchKeyword] = useState<string>('');
  const filteredManualCandidates = useMemo(() => {
    const kw = String(manualSearchKeyword || '').trim().toLowerCase();
    if (!kw) return manualCandidates;
    return (manualCandidates || []).filter((row: any) => {
      const unitCode = String(row?.unit_code || '').toLowerCase();
      const contentText = (Array.isArray(row?.content_lines) ? row.content_lines : [])
        .map((x: any) => String(x || '').toLowerCase())
        .join(' ');
      return unitCode.includes(kw) || contentText.includes(kw);
    });
  }, [manualCandidates, manualSearchKeyword]);
  // 步骤2（多拣货分拣）UI 状态（仅界面；逻辑稍后接入）
  const [overPickQty2, setOverPickQty2] = useState<number>(1);
  const [overPickOldCode2, setOverPickOldCode2] = useState<string>('');
  const [overPickSortRows, setOverPickSortRows] = useState<OverPickSortRow[]>([]);
  const [overPickGroups, setOverPickGroups] = useState<OverPickGroup[]>([]);
  const [overPickTmpLocHint, setOverPickTmpLocHint] = useState<number | null>(null);
  const overPickOldCodeRef = useRef<any>(null);
  const [labelPreviewOpen, setLabelPreviewOpen] = useState<boolean>(false);
  const [labelPreviewUrl, setLabelPreviewUrl] = useState<string>('');
  const [labelPreviewLoading, setLabelPreviewLoading] = useState<boolean>(false);
  const [labelPreviewTitle, setLabelPreviewTitle] = useState<string>('预览');
  const [unoperableHelpOpen, setUnoperableHelpOpen] = useState<boolean>(false);
  const canOperate = !!canOperateProp;
  const isBatchMode = !!isBatch;
  const isSortCountInstruction = useMemo(() => {
    if (isSortCountTemplate) return true;
    const detail = instructionDetail || {};
    const tplName = String(
      detail?.template_detail?.name
      || detail?.template_snapshot?.name
      || detail?.template_name
      || detail?.name
      || batchDetail?.template_name
      || '',
    ).trim().toLowerCase();
    const tplCode = String(
      detail?.template_detail?.system_code
      || detail?.template_snapshot?.system_code
      || detail?.template_detail?.code
      || detail?.template_snapshot?.code
      || detail?.template_system_code
      || detail?.template
      || batchDetail?.template_system_code
      || batchDetail?.template
      || '',
    ).trim().toLowerCase();
    const text = `${tplName} ${tplCode}`;
    return (
      text.includes('分拣清点')
      || text.includes('sort_count')
      || text.includes('sort-count')
      || text.includes('sortandcount')
      || text.includes('sort_and_count')
    );
  }, [batchDetail, instructionDetail, isSortCountTemplate]);
  const isRegularOutboundInstruction = useMemo(() => {
    const detail = instructionDetail || {};
    const tplName = String(
      detail?.template_detail?.name
      || detail?.template_snapshot?.name
      || detail?.template_name
      || detail?.name
      || batchDetail?.template_name
      || '',
    ).trim().toLowerCase();
    const tplCode = String(
      detail?.template_detail?.system_code
      || detail?.template_snapshot?.system_code
      || detail?.template_detail?.code
      || detail?.template_snapshot?.code
      || detail?.template_system_code
      || detail?.template
      || batchDetail?.template_system_code
      || batchDetail?.template
      || '',
    ).trim().toLowerCase();
    const text = `${tplName} ${tplCode}`;
    return text.includes('常规出库') || text.includes('regular_outbound') || text.includes('regular-outbound');
  }, [batchDetail, instructionDetail]);
  const isSingleDropshipInstruction = useMemo(() => {
    const detail = instructionDetail || {};
    const tplName = String(
      detail?.template_detail?.name
      || detail?.template_snapshot?.name
      || detail?.template_name
      || detail?.name
      || batchDetail?.template_name
      || '',
    ).trim().toLowerCase();
    const tplCode = String(
      detail?.template_detail?.system_code
      || detail?.template_snapshot?.system_code
      || detail?.template_detail?.code
      || detail?.template_snapshot?.code
      || detail?.template_system_code
      || detail?.template
      || batchDetail?.template_system_code
      || batchDetail?.template
      || '',
    ).trim().toLowerCase();
    const text = `${tplName} ${tplCode}`;
    return text.includes('一件代发') || text.includes('single_dropship') || text.includes('single-dropship');
  }, [batchDetail, instructionDetail]);
  const [regularOutboundStep1Ctx, setRegularOutboundStep1Ctx] = useState<{
    loaded: boolean;
    hasIocTarget: boolean;
  }>({
    loaded: false,
    hasIocTarget: false,
  });
  const sortCountDemandText = useMemo(() => {
    try {
      const detail = instructionDetail || {};
      const snapshot = detail?.questionnaire_snapshot || detail?.template_snapshot?.questionnaire || detail?.template_detail?.questionnaire || {};
      const rows = Array.isArray(snapshot?.questions)
        ? snapshot.questions
        : (Array.isArray(snapshot?.items) ? snapshot.items : []);
      const answers = detail?.customer_answers && typeof detail.customer_answers === 'object' ? detail.customer_answers : {};
      const demandQuestion = (Array.isArray(rows) ? rows : []).find((q: any) => {
        const key = String(q?.key || '').trim().toLowerCase();
        const label = String(q?.label || q?.title || '').trim();
        return key === 'sorting_requirement' || label === '分拣需求';
      });
      const demandKey = String(demandQuestion?.key || '').trim();
      const raw = String(
        (demandKey ? (answers as any)?.[demandKey] : undefined)
        || (answers as any)?.sorting_requirement
        || (data as any)?.sorting_requirement
        || (data as any)?.sort_count_demand
        || (data as any)?.demand_mode
        || '',
      ).trim();
      const normalized = raw.toLowerCase();
      if (normalized.includes('需求1') || normalized.includes('need1') || normalized.includes('混装')) {
        return '分拣需求1:分拣混装入库LIU/BIU为分装入库分拣组';
      }
      if (normalized.includes('需求2') || normalized.includes('need2') || normalized.includes('箱库存单元') || normalized.includes('托盘库存单元')) {
        return '分拣需求2: 原箱入库的BIU/PIU分拣为散货库存单元LIU';
      }
      if (normalized.includes('需求3') || normalized.includes('need3') || normalized.includes('再次细分') || normalized.includes('按照规则')) {
        return '分拣需求3: 单款LIU按产品特性细分';
      }
      return raw ? `当前分拣需求:${raw}` : '当前分拣需求:未识别';
    } catch {
      return '当前分拣需求:未识别';
    }
  }, [data, instructionDetail]);
  const sortCountDemandMode = useMemo<'need1' | 'need2' | 'need3' | ''>(() => {
    const raw = String(sortCountDemandText || '').trim().toLowerCase();
    if (!raw) return '';
    if (raw.includes('需求1') || raw.includes('need1') || raw.includes('混装')) return 'need1';
    if (raw.includes('需求2') || raw.includes('need2') || raw.includes('箱库存单元') || raw.includes('托盘库存单元')) return 'need2';
    if (raw.includes('需求3') || raw.includes('need3') || raw.includes('细分')) return 'need3';
    return '';
  }, [sortCountDemandText]);
  useEffect(() => {
    let cancelled = false;
    const parsePackContext = (raw: any) => {
      const root = (raw && typeof raw === 'object') ? raw : {};
      const snap1 = (root?.snapshot && typeof root.snapshot === 'object') ? root.snapshot : root;
      const snap = (snap1?.snapshot && typeof snap1.snapshot === 'object') ? snap1.snapshot : snap1;
      const sections = (snap?.sections && typeof snap.sections === 'object') ? snap.sections : {};
      const progressTabs = (sections?.progress_tabs && typeof sections.progress_tabs === 'object') ? sections.progress_tabs : {};
      const hasIocTarget = (typeof progressTabs?.has_ioc_target === 'boolean') ? !!progressTabs.has_ioc_target : false;
      return { hasIocTarget };
    };
    const run = async () => {
      if (!isRegularOutboundInstruction || !taskId || isSortCountInstruction) {
        if (!cancelled) {
          setRegularOutboundStep1Ctx({
            loaded: true,
            hasIocTarget: false,
          });
        }
        return;
      }
      try {
        const resp = await request.get(`/api/v1/instruction/exec/tasks/${taskId}/regular-outbound-pack-submissions/`, {
          params: { latest: 1, t: Date.now() } as any,
        });
        const payload = resp?.data?.result?.snapshot ?? resp?.data?.result ?? {};
        const parsed = parsePackContext(payload);
        if (!cancelled) {
          setRegularOutboundStep1Ctx({
            loaded: true,
            hasIocTarget: !!parsed.hasIocTarget,
          });
        }
      } catch {
        if (!cancelled) {
          setRegularOutboundStep1Ctx({
            loaded: true,
            hasIocTarget: false,
          });
        }
      }
    };
    void run();
    return () => { cancelled = true; };
  }, [isRegularOutboundInstruction, isSortCountInstruction, taskId]);
  const isRegularOutboundStep1BlockedByIocTarget = useMemo(
    () => isRegularOutboundInstruction && !isSortCountInstruction && !!regularOutboundStep1Ctx.hasIocTarget,
    [isRegularOutboundInstruction, isSortCountInstruction, regularOutboundStep1Ctx.hasIocTarget],
  );
  const isSingleDropshipStep1Blocked = useMemo(
    () => isSingleDropshipInstruction && !isSortCountInstruction,
    [isSingleDropshipInstruction, isSortCountInstruction],
  );
  const saveTimer = useRef<any>(null);
  // 步骤4：附件（后端持久化，跨刷新恢复）
  const [wrapupFileList, setWrapupFileList] = useState<any[]>([]);
  const [wrapupPreviewOpen, setWrapupPreviewOpen] = useState<boolean>(false);
  const [wrapupPreviewUrl, setWrapupPreviewUrl] = useState<string>('');
  const [wrapupPreviewTitle, setWrapupPreviewTitle] = useState<string>('附件预览');

  // 修复：避免 onChange 引用变化导致 useEffect 反复触发回写（从而饿死 TabsManager 的低优先级路由切换）
  const onChangeRef = useRef<any>(onChange);
  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);

  const focusOverPickOldCode = useCallback(() => {
    setTimeout(() => {
      try { overPickOldCodeRef.current?.focus?.(); } catch {}
      try { overPickOldCodeRef.current?.select?.(); } catch {}
    }, 0);
  }, []);

  // 步骤2：生成仓储容器前的“选择容器类型”弹窗
  const [overPickCreateOpen, setOverPickCreateOpen] = useState<boolean>(false);
  const [overPickCreateGroupIndex, setOverPickCreateGroupIndex] = useState<number | null>(null);
  const [overPickContainerTypeChoices, setOverPickContainerTypeChoices] = useState<Array<{ value: string; label: string }>>([]);
  const [overPickContainerTypeLoading, setOverPickContainerTypeLoading] = useState<boolean>(false);
  const [overPickContainerType, setOverPickContainerType] = useState<string>('box');

  const ensureStorageContainerTypeChoices = useCallback(async () => {
    if (overPickContainerTypeChoices.length > 0) return;
    try {
      setOverPickContainerTypeLoading(true);
      const resp = await request.get('/api/v1/storage/containers/type-choices/', { params: { t: Date.now() } as any });
      const list = Array.isArray(resp?.data?.results) ? resp.data.results : [];
      const def = String(resp?.data?.default || 'box') || 'box';
      setOverPickContainerTypeChoices(list);
      setOverPickContainerType(def);
    } catch {
      // 兜底：至少保证“箱子”存在
      setOverPickContainerTypeChoices([{ value: 'box', label: '箱子' }]);
      setOverPickContainerType('box');
    } finally {
      setOverPickContainerTypeLoading(false);
    }
  }, [overPickContainerTypeChoices.length]);

  // ===== 产出容器（权威口径）：从后端 DB 查询 IOC（InstructionOutputContainer）=====
  const [outputContainerCodes, setOutputContainerCodes] = useState<string[]>([]);
  const [outputContainerRowsByCode, setOutputContainerRowsByCode] = useState<Record<string, any>>({});
  const [outputContainersLoading, setOutputContainersLoading] = useState<boolean>(false);
  const [virtualConsumedOutputCodes, setVirtualConsumedOutputCodes] = useState<string[]>([]);
  const virtualConsumedOutputCodeSet = useMemo(() => {
    try {
      return new Set(
        (Array.isArray(virtualConsumedOutputCodes) ? virtualConsumedOutputCodes : [])
          .map((x) => String(x || '').trim().toUpperCase())
          .filter(Boolean),
      );
    } catch {
      return new Set<string>();
    }
  }, [virtualConsumedOutputCodes]);
  const availableOutputContainerCodes = useMemo(() => {
    try {
      return (outputContainerCodes || []).filter((c) => {
        const code = String(c || '').trim().toUpperCase();
        if (!code) return false;
        const row: any = (outputContainerRowsByCode || {})[code] || {};
        const st = String(row?.status || '').trim().toLowerCase();
        if (st === 'cancelled' || st === 'consumed') return false;
        if (virtualConsumedOutputCodeSet.has(code)) return false;
        return true;
      });
    } catch {
      return [] as string[];
    }
  }, [outputContainerCodes, outputContainerRowsByCode, virtualConsumedOutputCodeSet]);
  const hasOutputContainers = availableOutputContainerCodes.length > 0;
  const [instructionId, setInstructionId] = useState<number | null>(null);
  const [iocInstructionIdByCode, setIocInstructionIdByCode] = useState<Record<string, number>>({});
  const [hierarchyRowsFromStageSubs, setHierarchyRowsFromStageSubs] = useState<Array<{ parent_code: string; child_code: string }>>([]);
  const batchInstructionIds = useMemo(() => {
    if (!isBatchMode) return [] as number[];
    const rows = Array.isArray(batchDetail?.items)
      ? batchDetail.items
      : (Array.isArray(data?.items) ? data.items : []);
    const ids = (Array.isArray(rows) ? rows : [])
      .map((r: any) => Number(r?.instruction_id ?? r?.instruction ?? r?.instruction?.id ?? 0) || 0)
      .filter((id: number) => id > 0);
    return Array.from(new Set(ids));
  }, [isBatchMode, batchDetail?.items, data?.items]);
  const batchIocMapStorageKey = useMemo(() => {
    if (!isBatchMode) return '';
    const bid = Number(batchDetail?.id ?? data?.batch_id ?? data?.id ?? 0) || 0;
    return bid > 0 ? `dropship-pack-ioc-map-${bid}` : '';
  }, [isBatchMode, batchDetail?.id, data?.batch_id, data?.id]);

  useEffect(() => {
    (async () => {
      if (!taskId) return;
      if (isBatchMode) {
        setInstructionId(null);
        try {
          setOutputContainersLoading(true);
          const localCodes: string[] = [];
          if (batchIocMapStorageKey) {
            try {
              const raw = localStorage.getItem(batchIocMapStorageKey);
              const parsed = raw ? JSON.parse(raw) : null;
              if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
                Object.values(parsed).forEach((v: any) => {
                  const c = String(v || '').trim().toUpperCase();
                  if (c) localCodes.push(c);
                });
              }
            } catch {}
          }
          const mapByCode: Record<string, number> = {};
          const rowsByCode: Record<string, any> = {};
          const dbCodes: string[] = [];
          const jobs = await Promise.allSettled(
            batchInstructionIds.map((iid) => request.get('/api/v1/storage/output-containers/', {
              params: {
                instruction_id: iid,
                execution_session_id: String(executionSessionId || '').trim() || undefined,
                t: Date.now(),
              } as any,
            }))
          );
          jobs.forEach((j, idx) => {
            const iid = Number(batchInstructionIds[idx] || 0) || 0;
            if (j.status !== 'fulfilled' || !(iid > 0)) return;
            const list = Array.isArray(j.value?.data?.results) ? j.value.data.results : [];
            (Array.isArray(list) ? list : []).forEach((x: any) => {
              const code = String(x?.code || '').trim().toUpperCase();
              if (!code) return;
              dbCodes.push(code);
              mapByCode[code] = Number(x?.instruction_id || iid) || iid;
              rowsByCode[code] = { ...(x || {}), code };
            });
          });
          const merged = Array.from(new Set([...localCodes, ...dbCodes].filter(Boolean)));
          merged.sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
          merged.forEach((c) => {
            const code = String(c || '').trim().toUpperCase();
            if (!code) return;
            if (!rowsByCode[code]) rowsByCode[code] = { code };
          });
          setIocInstructionIdByCode(mapByCode);
          setOutputContainerRowsByCode(rowsByCode);
          setOutputContainerCodes(merged);
        } catch {
          setIocInstructionIdByCode({});
          setOutputContainerRowsByCode({});
          setOutputContainerCodes([]);
        } finally {
          setOutputContainersLoading(false);
        }
        return;
      }
      try {
        setOutputContainersLoading(true);
        // 1) 先取 task 的 instruction_id（权威）
        const tResp = await request.get(`/api/v1/instruction/exec/tasks/${taskId}/`, { params: { t: Date.now() } as any });
        const task = tResp?.data || {};
        const instrId =
          Number.isFinite(Number(task?.instruction_id)) ? Number(task.instruction_id) :
          Number.isFinite(Number(task?.instruction)) ? Number(task.instruction) :
          null;
        if (!instrId) {
              setInstructionId(null);
          setOutputContainerRowsByCode({});
          setOutputContainerCodes([]);
          return;
        }
            setInstructionId(instrId);
        // 2) 查询该指令的产出容器（IOC，DB 权威）
        const resp = await request.get('/api/v1/storage/output-containers/', {
          params: {
            instruction_id: instrId,
            execution_session_id: String(executionSessionId || '').trim() || undefined,
            t: Date.now(),
          } as any,
        });
        const list = Array.isArray(resp?.data?.results) ? resp.data.results : [];
        const codes = (Array.isArray(list) ? list : [])
          .map((x:any) => String(x?.code ?? '').trim().toUpperCase())
          .filter(Boolean);
        const mapByCode: Record<string, number> = {};
        const rowsByCode: Record<string, any> = {};
        (Array.isArray(list) ? list : []).forEach((x: any) => {
          const c = String(x?.code ?? '').trim().toUpperCase();
          if (!c) return;
          mapByCode[c] = Number(x?.instruction_id || instrId) || instrId;
          rowsByCode[c] = { ...(x || {}), code: c };
        });
        const uniq = Array.from(new Set(codes));
        uniq.sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
        setIocInstructionIdByCode(mapByCode);
        setOutputContainerRowsByCode(rowsByCode);
        setOutputContainerCodes(uniq);
      } catch {
        setInstructionId(null);
        setIocInstructionIdByCode({});
        setOutputContainerRowsByCode({});
        setOutputContainerCodes([]);
      } finally {
        setOutputContainersLoading(false);
      }
    })();
  }, [taskId, isBatchMode, executionSessionId, batchInstructionIds, batchIocMapStorageKey]);

  // 层级关系兜底：DB defer 场景下 parent/child 可能尚未落库，这里补拉“最新阶段快照”中的 by_hierarchy_rows（只用于展示分组/联动）
  useEffect(() => {
    (async () => {
      try {
        const instructionIds = (() => {
          if (isBatchMode) {
            return Array.from(new Set((batchInstructionIds || []).map((x) => Number(x || 0)).filter((x) => x > 0)));
          }
          const iid = Number(instructionId || 0);
          return iid > 0 ? [iid] : [];
        })();
        if (instructionIds.length === 0) {
          setHierarchyRowsFromStageSubs([]);
          setVirtualConsumedOutputCodes([]);
          return;
        }
        const isAfterPackMode = (raw: any): boolean => {
          const txt = String(raw || '').trim();
          if (!txt) return false;
          const low = txt.toLowerCase();
          return low === 'after_pack' || txt.includes('先申请包装数据');
        };
        const extractVirtualConsumedCodesFromSubmission = (sub: any): string[] => {
          try {
            const stageKey = String(sub?.stage_key || '').trim().toUpperCase();
            const snapRoot = (sub?.snapshot && typeof sub.snapshot === 'object') ? sub.snapshot : {};
            const snap = (snapRoot?.snapshot && typeof snapRoot.snapshot === 'object') ? snapRoot.snapshot : snapRoot;
            const kind = String((snapRoot as any)?.kind || (snap as any)?.kind || '').trim().toLowerCase();
            const isRegularOutboundStage = stageKey === 'REGULAR_OUTBOUND_PACK' || kind === 'regular_outbound_pack';
            if (!isRegularOutboundStage) return [];
            const metrics = (snap?.metrics && typeof snap.metrics === 'object') ? snap.metrics : {};
            const completionAudit = (metrics?.completion_mark_audit && typeof metrics.completion_mark_audit === 'object')
              ? metrics.completion_mark_audit
              : {};
            const waybillMode = String(
              metrics?.waybill_submit_mode
              || completionAudit?.waybill_submit_mode
              || '',
            ).trim();
            if (isAfterPackMode(waybillMode)) return [];
            const generated = Array.isArray(completionAudit?.generated_ioc_codes)
              ? completionAudit.generated_ioc_codes
              : [];
            return generated
              .map((x: any) => String(x || '').trim().toUpperCase())
              .filter(Boolean);
          } catch {
            return [];
          }
        };
        const parseRowsFromSubmission = (sub: any): Array<{ parent_code: string; child_code: string }> => {
          const out: Array<{ parent_code: string; child_code: string }> = [];
          const push = (pRaw: any, cRaw: any) => {
            const p = String(pRaw || '').trim().toUpperCase();
            const c = String(cRaw || '').trim().toUpperCase();
            if (!p || !c || p === c) return;
            out.push({ parent_code: p, child_code: c });
          };
          try {
            const snapRoot = (sub?.snapshot && typeof sub.snapshot === 'object') ? sub.snapshot : {};
            const snap = (snapRoot?.snapshot && typeof snapRoot.snapshot === 'object') ? snapRoot.snapshot : snapRoot;
            const sections = (snap?.sections && typeof snap.sections === 'object') ? snap.sections : {};
            const progressTabs = (sections?.progress_tabs && typeof sections.progress_tabs === 'object')
              ? sections.progress_tabs
              : ((snap?.metrics?.progress_tabs_snapshot && typeof snap.metrics.progress_tabs_snapshot === 'object')
                ? snap.metrics.progress_tabs_snapshot
                : {});
            const rows = Array.isArray(progressTabs?.by_hierarchy_rows) ? progressTabs.by_hierarchy_rows : [];
            rows.forEach((r: any) => push(r?.parent_code, r?.child_code));
          } catch {}
          return out;
        };
        const jobs = await Promise.allSettled(
          instructionIds.map((iid) => request.get(`/api/v1/instruction/instructions/${iid}/stage-submissions`, {
            params: { latest: true, t: Date.now() } as any,
          })),
        );
        const merged: Array<{ parent_code: string; child_code: string }> = [];
        const seen = new Set<string>();
        const virtualConsumedSet = new Set<string>();
        jobs.forEach((j) => {
          if (j.status !== 'fulfilled') return;
          const list = Array.isArray(j.value?.data?.results) ? j.value.data.results : [];
          list.forEach((sub: any) => {
            extractVirtualConsumedCodesFromSubmission(sub).forEach((code) => {
              const c = String(code || '').trim().toUpperCase();
              if (!c) return;
              virtualConsumedSet.add(c);
            });
            const rows = parseRowsFromSubmission(sub);
            rows.forEach((ln) => {
              const p = String(ln?.parent_code || '').trim().toUpperCase();
              const c = String(ln?.child_code || '').trim().toUpperCase();
              if (!p || !c || p === c) return;
              const key = `${p}>>${c}`;
              if (seen.has(key)) return;
              seen.add(key);
              merged.push({ parent_code: p, child_code: c });
            });
          });
        });
        setHierarchyRowsFromStageSubs(merged);
        setVirtualConsumedOutputCodes(Array.from(virtualConsumedSet));
      } catch {
        setHierarchyRowsFromStageSubs([]);
        setVirtualConsumedOutputCodes([]);
      }
    })();
  }, [instructionId, isBatchMode, batchInstructionIds]);

  // 步骤4：加载已上传附件（按 step 维度）
  useEffect(() => {
    (async () => {
      try {
        if (isBatchMode) {
          setWrapupFileList([]);
          return;
        }
        const sid = Number(stepId || 0);
        if (!(sid > 0)) return;
        // 注意：后端默认分页 page_size=10；这里必须拉全量，否则会把本地列表覆盖成前 10 条。
        const pageSize = 1000;
        let page = 1;
        let all: any[] = [];
        for (let guard = 0; guard < 50; guard += 1) {
          const resp = await request.get('/api/v1/instruction/exec/step-attachments/', {
            params: {
              step: sid,
              status: 'active',
              execution_session_id: String(executionSessionId || '').trim() || undefined,
              ordering: '-created_at',
              page,
              page_size: pageSize,
              t: Date.now(),
            } as any
          });
          const list = Array.isArray(resp?.data?.results) ? resp.data.results : (Array.isArray(resp?.data) ? resp.data : []);
          const rows = Array.isArray(list) ? list : [];
          all = all.concat(rows);
          const hasNext = !!(resp?.data && (resp.data as any).next);
          if (!hasNext || rows.length === 0) break;
          page += 1;
        }
        const files = (all || []).map((x: any) => {
          const id = String(x?.id ?? '');
          const url = String(x?.file || '').trim();
          const name = String(x?.file_name || x?.fileName || '').trim() || (url ? url.split('/').pop() : '') || `附件#${id}`;
          return {
            uid: id || `${Date.now()}_${Math.random()}`,
            name,
            status: 'done',
            url,
            response: x,
          };
        });
        setWrapupFileList(files);
      } catch {}
    })();
  }, [stepId, isBatchMode, executionSessionId]);

  // 步骤4：附件分页（仅影响显示，不影响真实附件数量）
  const [wrapupAttPage, setWrapupAttPage] = useState<number>(1);
  const [wrapupAttPageSize, setWrapupAttPageSize] = useState<number>(10);
  useEffect(() => {
    try {
      const total = (wrapupFileList || []).length;
      const max = Math.max(1, Math.ceil(total / wrapupAttPageSize));
      if (wrapupAttPage > max) setWrapupAttPage(1);
    } catch {}
  }, [wrapupFileList.length, wrapupAttPageSize]); // eslint-disable-line react-hooks/exhaustive-deps

  // 扫码 UI 状态（仅前端）
  const [scanOutContainer, setScanOutContainer] = useState<string>('');
  const [scanLocation, setScanLocation] = useState<string>('');
  const [selectedScanned, setSelectedScanned] = useState<React.Key[]>([]);
  const [putAwayLocByContainer, setPutAwayLocByContainer] = useState<Record<string, string>>({});
  const scanOutRef = useRef<any>(null);
  const scanLocRef = useRef<any>(null);
  const [putAwayActiveTab, setPutAwayActiveTab] = useState<'ioc_normal' | 'ioc_exc' | 'sc'>('ioc_normal');

  const focusContainerInput = useCallback(() => {
    setTimeout(() => {
      try { scanOutRef.current?.focus?.(); } catch {}
      try { scanOutRef.current?.select?.(); } catch {}
    }, 0);
  }, []);

  const scCodes = useMemo(() => {
    try {
      const arr = Array.from(new Set(
        (overPickGroups || [])
          .map((g: any) => String(g?.created_container_code || '').trim().toUpperCase())
          .filter(Boolean)
      ));
      arr.sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
      return arr;
    } catch {
      return [];
    }
  }, [overPickGroups]);
  // 分拣清点：操作阶段产出的是 SC（非 IOC），用于步骤3第一个 Tab 展示
  const [sortCountStageScCodes, setSortCountStageScCodes] = useState<string[]>([]);
  useEffect(() => {
    (async () => {
      if (!isSortCountInstruction) { setSortCountStageScCodes([]); return; }
      if (isBatchMode) { setSortCountStageScCodes([]); return; }
      const instrId = Number(instructionId || 0);
      if (!(instrId > 0)) { setSortCountStageScCodes([]); return; }
      try {
        const resp = await request.get('/api/v1/storage/containers/sort-count/drafts/', {
          params: { instruction_id: instrId, t: Date.now() } as any,
        });
        const list = Array.isArray(resp?.data?.results) ? resp.data.results : [];
        const arr = Array.from(new Set(
          (Array.isArray(list) ? list : [])
            .map((x: any) => String(x?.system_code || '').trim().toUpperCase())
            .filter(Boolean)
        ));
        arr.sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
        setSortCountStageScCodes(arr);
      } catch {
        setSortCountStageScCodes([]);
      }
    })();
  }, [isSortCountInstruction, isBatchMode, instructionId]);
  const isOperationTabStorage = isSortCountInstruction && putAwayActiveTab === 'ioc_normal';

  const exceptionCodeSet = useMemo(() => (
    new Set((exceptionContainers || []).map((x: any) => String(x?.code || '').trim().toUpperCase()).filter(Boolean))
  ), [exceptionContainers]);
  const iocExceptionCodes = useMemo(
    () => (availableOutputContainerCodes || []).filter((c) => exceptionCodeSet.has(String(c || '').trim().toUpperCase())),
    [availableOutputContainerCodes, exceptionCodeSet],
  );
  const iocNormalCodes = useMemo(
    () => (availableOutputContainerCodes || []).filter((c) => !exceptionCodeSet.has(String(c || '').trim().toUpperCase())),
    [availableOutputContainerCodes, exceptionCodeSet],
  );
  const operationGeneratedCodes = useMemo(
    () => (isSortCountInstruction ? sortCountStageScCodes : iocNormalCodes),
    [isSortCountInstruction, sortCountStageScCodes, iocNormalCodes],
  );
  const allPutAwayCodes = useMemo(() => {
    try {
      const set = new Set<string>();
      (iocExceptionCodes || []).forEach((c) => { const cc = String(c || '').trim().toUpperCase(); if (cc) set.add(cc); });
      (operationGeneratedCodes || []).forEach((c) => { const cc = String(c || '').trim().toUpperCase(); if (cc) set.add(cc); });
      (scCodes || []).forEach((c) => { const cc = String(c || '').trim().toUpperCase(); if (cc) set.add(cc); });
      const arr = Array.from(set);
      arr.sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
      return arr;
    } catch {
      return [];
    }
  }, [iocExceptionCodes, operationGeneratedCodes, scCodes]);
  // 统计信息：总数/已放入/未放入（IOC + SC）
  const putAwayStats = useMemo(() => {
    const total = allPutAwayCodes.length;
    let done = 0;
    allPutAwayCodes.forEach((c) => {
      const loc = String(putAwayLocByContainer?.[c] || '').trim();
      if (loc) done += 1;
    });
    const todo = Math.max(total - done, 0);
    return { total, done, todo };
  }, [allPutAwayCodes, putAwayLocByContainer]);
  const putAwayGate = useMemo(() => ({
    total: Number(putAwayStats.total || 0),
    done: Number(putAwayStats.done || 0),
    todo: Number(putAwayStats.todo || 0),
    all_done: Number(putAwayStats.total || 0) > 0
      ? Number(putAwayStats.todo || 0) === 0
      : true,
  }), [putAwayStats]);
  const iocHierarchyLinks = useMemo(() => {
    const out: Array<{ parent_code: string; child_code: string }> = [];
    const seen = new Set<string>();
    const push = (pRaw: any, cRaw: any) => {
      const p = String(pRaw || '').trim().toUpperCase();
      const c = String(cRaw || '').trim().toUpperCase();
      if (!p || !c || p === c) return;
      const k = `${p}>>${c}`;
      if (seen.has(k)) return;
      seen.add(k);
      out.push({ parent_code: p, child_code: c });
    };
    const collect = (rows: any) => {
      if (!Array.isArray(rows)) return;
      rows.forEach((r: any) => {
        if (!r || typeof r !== 'object') return;
        push(r?.parent_code, r?.child_code);
      });
    };
    Object.values(outputContainerRowsByCode || {}).forEach((row: any) => {
      if (!row || typeof row !== 'object') return;
      const code = String(row?.code || '').trim().toUpperCase();
      if (!code) return;
      if (row?.parent_code) push(row.parent_code, code);
      const childCodes = Array.isArray(row?.child_codes) ? row.child_codes : [];
      childCodes.forEach((ch: any) => push(code, ch));
    });
    collect((metricsProp as any)?.hierarchy_links);
    collect((data as any)?.hierarchy_links);
    collect((step as any)?.data?.hierarchy_links);
    collect((metricsProp as any)?.progress_tabs_snapshot?.by_hierarchy_rows);
    collect((data as any)?.progress_tabs_snapshot?.by_hierarchy_rows);
    collect((step as any)?.data?.progress_tabs_snapshot?.by_hierarchy_rows);
    collect((metricsProp as any)?.sections?.progress_tabs?.by_hierarchy_rows);
    collect((data as any)?.sections?.progress_tabs?.by_hierarchy_rows);
    collect((step as any)?.data?.sections?.progress_tabs?.by_hierarchy_rows);
    collect(hierarchyRowsFromStageSubs);
    return out;
  }, [outputContainerRowsByCode, metricsProp, data, step, hierarchyRowsFromStageSubs]);
  const iocNormalBundleMeta = useMemo(() => {
    const normalSet = new Set((iocNormalCodes || []).map((x) => String(x || '').trim().toUpperCase()).filter(Boolean));
    const adj = new Map<string, Set<string>>();
    const parentToChildren = new Map<string, Set<string>>();
    const childToParents = new Map<string, Set<string>>();
    normalSet.forEach((c) => {
      adj.set(c, new Set<string>());
      parentToChildren.set(c, new Set<string>());
    });
    (iocHierarchyLinks || []).forEach((ln: any) => {
      const p = String(ln?.parent_code || '').trim().toUpperCase();
      const c = String(ln?.child_code || '').trim().toUpperCase();
      if (!normalSet.has(p) || !normalSet.has(c) || p === c) return;
      adj.get(p)?.add(c);
      adj.get(c)?.add(p);
      parentToChildren.get(p)?.add(c);
      if (!childToParents.has(c)) childToParents.set(c, new Set<string>());
      childToParents.get(c)?.add(p);
    });
    const visited = new Set<string>();
    const bundleByCode: Record<string, string[]> = {};
    const groupedBundles: Array<{ parentCode: string; childCodes: string[]; allCodes: string[] }> = [];
    const standaloneCodes: string[] = [];
    const sortedAll = Array.from(normalSet).sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
    sortedAll.forEach((start) => {
      if (visited.has(start)) return;
      const stack = [start];
      const comp: string[] = [];
      visited.add(start);
      while (stack.length > 0) {
        const cur = String(stack.pop() || '').trim().toUpperCase();
        if (!cur) continue;
        comp.push(cur);
        const nb = adj.get(cur) || new Set<string>();
        nb.forEach((nx) => {
          if (!visited.has(nx)) {
            visited.add(nx);
            stack.push(nx);
          }
        });
      }
      comp.sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
      if (comp.length <= 1) {
        const only = comp[0];
        if (only) {
          standaloneCodes.push(only);
          bundleByCode[only] = [only];
        }
        return;
      }
      const roots = comp.filter((c) => !(childToParents.get(c)?.size || 0));
      roots.sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
      const parentCode = roots[0] || comp[0];
      const childSet = new Set<string>(comp.filter((c) => c !== parentCode));
      const bfs = [parentCode];
      const touched = new Set<string>([parentCode]);
      while (bfs.length > 0) {
        const p = String(bfs.shift() || '').trim().toUpperCase();
        if (!p) continue;
        (parentToChildren.get(p) || new Set<string>()).forEach((ch) => {
          if (!normalSet.has(ch) || !childSet.has(ch) || touched.has(ch)) return;
          touched.add(ch);
          bfs.push(ch);
        });
      }
      const childCodes = Array.from(childSet).sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
      const allCodes = [parentCode, ...childCodes];
      allCodes.forEach((c) => {
        bundleByCode[c] = allCodes;
      });
      groupedBundles.push({ parentCode, childCodes, allCodes });
    });
    groupedBundles.sort((a, b) => a.parentCode.localeCompare(b.parentCode, undefined, { numeric: true, sensitivity: 'base' }));
    standaloneCodes.sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
    return { bundleByCode, groupedBundles, standaloneCodes };
  }, [iocNormalCodes, iocHierarchyLinks]);
  const toggleIocNormalBundleSelection = useCallback((codeRaw: any, checked: boolean) => {
    const code = String(codeRaw || '').trim().toUpperCase();
    if (!code) return;
    const bundle = (iocNormalBundleMeta.bundleByCode as any)?.[code];
    const targets = (Array.isArray(bundle) && bundle.length > 0
      ? bundle
      : [code]
    ).map((x: any) => String(x || '').trim().toUpperCase()).filter(Boolean);
    setSelectedScanned((prev) => {
      const set = new Set((prev || []).map((x) => String(x || '').trim().toUpperCase()).filter(Boolean));
      if (checked) targets.forEach((x) => set.add(x));
      else targets.forEach((x) => set.delete(x));
      return Array.from(set);
    });
  }, [iocNormalBundleMeta.bundleByCode]);

  // 将 map 同步为 putAwayOps（用于提交与草稿）
  useEffect(() => {
    try {
      const ops: PutAwayOp[] = Object.keys(putAwayLocByContainer || {}).map((c) => ({
        container_code: c,
        location_code: String(putAwayLocByContainer[c] || '').trim(),
      })).filter(x => String(x.container_code || '').trim() && String(x.location_code || '').trim());
      setPutAwayOps(ops);
    } catch {}
  }, [putAwayLocByContainer]);

  const addScannedContainer = useCallback((raw: string) => {
    const code = String(raw || '').trim().toUpperCase();
    if (!code) return;
    // 按当前 Tab 限定可选容器范围，避免跨类混选
    if (putAwayActiveTab === 'sc') {
      if ((scCodes || []).length > 0 && !(scCodes || []).includes(code)) {
        message.warning('该容器不在多拣货产品生成的容器列表中');
        return;
      }
    } else if (isOperationTabStorage) {
      if ((operationGeneratedCodes || []).length > 0 && !(operationGeneratedCodes || []).includes(code)) {
        message.warning('该容器不在操作过程生成的容器列表中');
        return;
      }
    } else {
      // IOC：若存在权威列表，则只允许扫这些
      if (hasOutputContainers && !availableOutputContainerCodes.includes(code)) {
        message.warning('该容器不在产出容器列表中');
        return;
      }
    }
    setSelectedScanned(prev => {
      const next = new Set((prev || []).map((x) => String(x || '').trim().toUpperCase()).filter(Boolean));
      const bundle = (putAwayActiveTab === 'ioc_normal')
        ? ((iocNormalBundleMeta.bundleByCode as any)?.[code] || [code])
        : [code];
      (Array.isArray(bundle) ? bundle : [code]).forEach((x: any) => {
        const c = String(x || '').trim().toUpperCase();
        if (c) next.add(c);
      });
      return Array.from(next);
    });
    // 扫描/输入容器后：清空输入框并聚焦，便于连续扫描
    setScanOutContainer('');
    focusContainerInput();
  }, [hasOutputContainers, availableOutputContainerCodes, putAwayActiveTab, scCodes, focusContainerInput, iocNormalBundleMeta.bundleByCode, isOperationTabStorage, operationGeneratedCodes]);

  const applyLocationToSelected = useCallback((rawLoc: string) => {
    const loc = String(rawLoc || '').trim().toUpperCase();
    const selected = selectedScanned.map(String).filter(Boolean);
    const allowedSet = new Set<string>(
      ((putAwayActiveTab === 'sc' || isOperationTabStorage) ? (putAwayActiveTab === 'sc' ? scCodes : operationGeneratedCodes) : availableOutputContainerCodes)
        .map((x) => String(x || '').trim().toUpperCase())
        .filter(Boolean)
    );
    const targets = selected.filter((c) => {
      const cc = String(c || '').trim().toUpperCase();
      if (!cc) return false;
      // 若列表为空，则不限制（兜底）；否则限制在本 Tab 列表内
      if (allowedSet.size === 0) return true;
      return allowedSet.has(cc);
    });
    if (!targets || targets.length === 0) {
      // 不打断流程：仅提示
      message.warning('请先在当前页签内选择/扫描容器（可多选）');
      return;
    }
    (async () => {
      try {
        if (putAwayActiveTab === 'sc' || isOperationTabStorage) {
          // 仓储容器（SC）：必须是 DB 存在的仓位编码，走“上架记录”接口
          if (!loc) {
            message.warning('仓储容器上架必须输入仓位编码');
            return;
          }
          const resp = await request.post('/api/v1/storage/put-away/create/', {
            container_codes: targets,
            location_code: loc,
            remarks: '',
          });
          const ok = !!resp?.data?.success;
          if (!ok) {
            message.error(resp?.data?.message || '创建上架记录失败');
            return;
          }
          const rows = Array.isArray(resp?.data?.data) ? resp.data.data : [];
          setPutAwayLocByContainer(prev => {
            const next = { ...(prev || {}) };
            rows.forEach((r: any) => {
              const c = String(r?.container_code || '').trim().toUpperCase();
              const lc = String(r?.location_code || '').trim().toUpperCase();
              if (!c) return;
              if (lc) next[c] = lc;
            });
            return next;
          });
        } else {
          // IOC：仓位允许为空/任意文本（后端自动识别正式仓位 vs 文本）
          let results: any[] = [];
          if (isBatchMode) {
            const grouped = new Map<number, string[]>();
            const unresolved: string[] = [];
            targets.forEach((code) => {
              const c = String(code || '').trim().toUpperCase();
              if (!c) return;
              const iid = Number(iocInstructionIdByCode?.[c] || 0) || 0;
              if (!(iid > 0)) {
                unresolved.push(c);
                return;
              }
              if (!grouped.has(iid)) grouped.set(iid, []);
              grouped.get(iid)!.push(c);
            });
            if (grouped.size === 0) {
              throw new Error(unresolved.length > 0 ? '未能识别所选 IOC 对应的指令，请先重新打开收尾页面刷新容器数据' : '未选择可提交的 IOC');
            }
            const reqs = Array.from(grouped.entries()).map(([iid, codes]) =>
              request.post('/api/v1/storage/output-containers/put-away/', {
                instruction_id: iid,
                codes,
                location_input: loc,
              })
            );
            const settled = await Promise.allSettled(reqs);
            let failGroups = 0;
            settled.forEach((r) => {
              if (r.status === 'fulfilled') {
                const rows = Array.isArray(r.value?.data?.results) ? r.value.data.results : [];
                results = results.concat(rows);
              } else {
                failGroups += 1;
              }
            });
            if (unresolved.length > 0) {
              message.warning(`有 ${unresolved.length} 个 IOC 无法匹配到指令，已跳过`);
            }
            if (failGroups > 0) {
              message.warning(`有 ${failGroups} 组 IOC 仓位提交失败，请重试`);
            }
          } else {
            if (!instructionId) throw new Error('instructionId 缺失，无法提交仓位');
            const resp = await request.post('/api/v1/storage/output-containers/put-away/', {
              instruction_id: instructionId,
              codes: targets,
              location_input: loc,
            });
            results = Array.isArray(resp?.data?.results) ? resp.data.results : [];
          }
          // 用后端返回的 location_display 回写 UI（兼容“匹配正式仓位/临时文本/清空”）
          setPutAwayLocByContainer(prev => {
            const next = { ...(prev || {}) };
            results.forEach((r:any) => {
              const c = String(r?.code || '').trim().toUpperCase();
              const disp = String(r?.location_display || '').trim().toUpperCase();
              if (!c) return;
              if (disp) next[c] = disp;
              else delete next[c];
            });
            return next;
          });
        }
        // 执行后：清空两个输入框、清空选定状态，并回到“扫描容器”
        setScanOutContainer('');
        setScanLocation('');
        setSelectedScanned([]);
        focusContainerInput();
      } catch (e:any) {
        const data = e?.response?.data;
        // 兼容 put-away/create 的校验错误结构
        if (data && typeof data === 'object' && data?.errors) {
          const errs = data.errors || {};
          const msgs: string[] = [];
          Object.keys(errs).forEach((k) => {
            const v = (errs as any)[k];
            if (Array.isArray(v)) msgs.push(`${k}: ${v.join(', ')}`);
            else if (v) msgs.push(`${k}: ${String(v)}`);
          });
          Modal.error({
            title: '提交仓位失败',
            content: msgs.length > 0 ? msgs.join('\n') : (data?.message || '数据验证失败'),
            okText: '知道了',
          });
          return;
        }
        message.error(data?.detail || data?.message || e?.message || '提交仓位失败');
      }
    })();
  }, [selectedScanned, putAwayActiveTab, scCodes, availableOutputContainerCodes, instructionId, focusContainerInput, isBatchMode, iocInstructionIdByCode, isOperationTabStorage, operationGeneratedCodes]);

  const openAllocationsModal = useCallback((code: string, allocs: ExceptionAllocRow[]) => {
    setAllocModalTitle(String(code || ''));
    setAllocModalRows(Array.isArray(allocs) ? allocs : []);
    setAllocModalOpen(true);
  }, []);

  const openOutputContainerLabelPreview = useCallback(async (code: string, allocs: ExceptionAllocRow[]) => {
    const cc = String(code || '').trim().toUpperCase();
    if (!cc) return;
    if (!instructionId) { message.error('instructionId 缺失，无法生成箱标预览'); return; }
    try {
      if (labelPreviewUrl) { try { URL.revokeObjectURL(labelPreviewUrl); } catch {} }
      setLabelPreviewLoading(true);
      setLabelPreviewTitle('箱标预览（4x6）');
      // 复用换标+装箱阶段的箱标样式/字段：后端 label service 以 payload 渲染
      const details = (Array.isArray(allocs) ? allocs : [])
        .map((a) => ({ new_code: String(a?.unit_code || '').trim(), qty: Number(a?.quantity || 0) || 0 }))
        .filter((x) => x.new_code && x.qty > 0)
        .sort((a, b) => (b.qty || 0) - (a.qty || 0));
      const totalQty = details.reduce((s, x) => s + (Number(x.qty || 0) || 0), 0);
      const newKinds = details.length;
      const containerTypeDisplay = (() => {
        const seg = cc.split('-');
        const mid = String(seg?.[1] || '').trim().toUpperCase();
        if (mid === 'B') return '纸箱';
        if (mid === 'P') return '托盘';
        if (mid === 'O') return '其他';
        return mid || '-';
      })();
      const payload: any = {
        instruction_id: instructionId,
        container_code: cc,
        instruction_code: String(instructionCodeFromParent || '').trim(),
        project_summary: '', // 让后端根据 instruction_id 自动补齐项目编码/名称
        label_variant: 'exception',
        container_type_display: containerTypeDisplay,
        created_at: (() => { try { return new Date().toLocaleString(); } catch { return ''; } })(),
        dims_cm: { l: '-', w: '-', h: '-' },
        dims_in: { l: '-', w: '-', h: '-' },
        weight: { kg: '-', lb: '-' },
        new_kinds: newKinds,
        total_qty: totalQty,
        details,
      };
      const resp = await request.post('/api/v1/storage/output-containers/labels/preview/', payload, { responseType: 'blob' as any });
      const blob = (resp as any)?.data as Blob;
      const url = URL.createObjectURL(blob);
      setLabelPreviewUrl(url);
      setLabelPreviewOpen(true);
    } catch (e:any) {
      message.error(e?.response?.data?.error || e?.response?.data?.detail || e?.message || '生成箱标预览失败');
    } finally {
      setLabelPreviewLoading(false);
    }
  }, [instructionId, instructionCodeFromParent, labelPreviewUrl]);

  const openStorageContainerLabelPreview = useCallback(async (containerId: number) => {
    const cid = Number(containerId || 0);
    if (!(cid > 0)) return;
    try {
      if (labelPreviewUrl) { try { URL.revokeObjectURL(labelPreviewUrl); } catch {} }
      setLabelPreviewLoading(true);
      setLabelPreviewTitle('仓储容器标签预览');
      const resp = await request.post('/api/v1/storage/containers/labels/preview/', { container_id: cid }, { responseType: 'blob' as any });
      const blob = (resp as any)?.data as Blob;
      const url = URL.createObjectURL(blob);
      setLabelPreviewUrl(url);
      setLabelPreviewOpen(true);
    } catch (e:any) {
      message.error(e?.response?.data?.error || e?.response?.data?.detail || e?.message || '生成仓储容器标签预览失败');
    } finally {
      setLabelPreviewLoading(false);
    }
  }, [labelPreviewUrl]);

  const calcNextTempLocNo = useCallback((groups: OverPickGroup[]) => {
    try {
      const maxNo = Math.max(0, ...(groups || []).map((g:any) => Number(g?.temp_loc_no || 0) || 0));
      return maxNo + 1;
    } catch {
      return 1;
    }
  }, []);

  const printStorageContainerLabel = useCallback(async (containerId: number) => {
    const cid = Number(containerId || 0);
    if (!(cid > 0)) return;
    try {
      await request.post('/api/v1/storage/containers/labels/print/', {
        container_id: cid,
        print_options: { copies: 1, direct_print: true },
      });
      message.success('已触发打印（仓储容器标签）');
    } catch (e:any) {
      message.error(e?.response?.data?.message || e?.response?.data?.error || e?.message || '打印失败');
    }
  }, []);

  const createStorageContainerForOverPickGroup = useCallback(async (groupIndex: number, containerType: string) => {
    const gi = Number(groupIndex || 0);
    const g = overPickGroups?.[gi];
    if (!g) return;
    if (!canOperate) return;
    if (g.created_container_id) {
      // 已生成：直接预览/打印
      await openStorageContainerLabelPreview(g.created_container_id);
      await printStorageContainerLabel(g.created_container_id);
      focusOverPickOldCode();
      return;
    }
    const groupCode = String(g.group_code || '').trim();
    if (!groupCode) { message.error('分拣组为空，无法生成容器'); return; }

    // 聚合：IU -> qty
    const agg: Record<string, number> = {};
    (g.items || []).forEach((it) => {
      const iu = String(it?.inventory_unit_code || '').trim();
      const q = Math.floor(Number(it?.qty || 0) || 0);
      if (!iu || !(q > 0)) return;
      agg[iu] = (agg[iu] || 0) + q;
    });
    const items = Object.entries(agg).map(([inventory_unit_code, quantity]) => ({ inventory_unit_code, quantity }));
    if (items.length === 0) { message.error('该分拣组无可生成容器的有效数量'); return; }

    try {
      const resp = isBatchMode
        ? await request.post(`/api/v1/instruction/batch-exec/tasks/${taskId}/wrap-up/overpick-create-container/`, {
            group_system_code: groupCode,
            instruction_id: Number(g?.instruction_id || 0) || undefined,
            container_type: String(containerType || 'box'),
            remarks: '',
            items,
          })
        : await request.post('/api/v1/storage/containers/create-from-overpick/', {
            task_id: taskId,
            instruction_id: instructionId,
            group_system_code: groupCode,
            container_type: String(containerType || 'box'),
            remarks: '',
            items,
          });
      const cid = Number(resp?.data?.container?.id || 0) || 0;
      const ccode = String(resp?.data?.container?.system_code || '').trim();
      if (!(cid > 0)) { message.error('生成容器失败：未返回容器ID'); return; }

      setOverPickGroups(prev => (prev || []).map((x, idx) => (idx === gi ? { ...x, created_container_id: cid, created_container_code: ccode } : x)));
      message.success(`仓储容器已生成：${ccode || `#${cid}`}`);

      await openStorageContainerLabelPreview(cid);
      await printStorageContainerLabel(cid);
      focusOverPickOldCode();
    } catch (e:any) {
      Modal.warning({
        title: '生成容器失败',
        content: String(e?.response?.data?.detail || e?.response?.data?.error || e?.message || '生成失败'),
        okText: '知道了',
      });
      focusOverPickOldCode();
    }
  }, [overPickGroups, canOperate, taskId, instructionId, isBatchMode, openStorageContainerLabelPreview, printStorageContainerLabel, focusOverPickOldCode]);

  // 步骤2：提交一次“分拣”（根据旧条码聚合到分拣组；同条码累加数量）
  const submitOverPickSort = useCallback(async () => {
    if (!canOperate) return;
    const qty = Math.floor(Number(overPickQty2 || 0) || 0) || 1;
    const oldRaw = String(overPickOldCode2 || '').trim();
    if (!oldRaw) { message.warning('请扫描旧条码'); focusOverPickOldCode(); return; }
    const oldCode = oldRaw.toUpperCase();

    // 1) 若条码已存在：累加并提示临时分拣仓位
    const existed = (() => {
      for (let gi = 0; gi < overPickGroups.length; gi += 1) {
        const g = overPickGroups[gi];
        // 已生成容器的分拣组：视为封存；不在此处累加（应新建一条新的临时分拣仓位记录）
        if (Number((g as any)?.created_container_id || 0) > 0) continue;
        for (let ii = 0; ii < (g.items || []).length; ii += 1) {
          const it = g.items[ii];
          if (String(it?.old_code || '').trim().toUpperCase() === oldCode) {
            return { gi, ii };
          }
        }
      }
      return null;
    })();
    if (existed) {
      setOverPickGroups(prev => prev.map((g, gi) => {
        if (gi !== existed.gi) return g;
        const items = (g.items || []).map((it, ii) => {
          if (ii !== existed.ii) return it;
          return { ...it, qty: Math.floor(Number(it.qty || 0) || 0) + qty };
        });
        return { ...g, items };
      }));
      try {
        const no = Number(overPickGroups?.[existed.gi]?.temp_loc_no || 0) || (existed.gi + 1);
        setOverPickTmpLocHint(no);
      } catch {
        setOverPickTmpLocHint(existed.gi + 1);
      }
      setOverPickOldCode2('');
      setOverPickQty2(1);
      focusOverPickOldCode();
      return;
    }

    // 2) 新条码：解析到 IU -> sorting_group（多IU命中取“最后一个IU”由后端保证）
    try {
      const resp = isBatchMode
        ? await request.post(`/api/v1/instruction/batch-exec/tasks/${taskId}/wrap-up/resolve-old-code/`, {
            old_code: oldCode,
          })
        : await request.post('/api/v1/storage/over-pick/resolve-old-code/', {
            task_id: taskId,
            instruction_id: instructionId,
            old_code: oldCode,
          });
      const sgCode = String(resp?.data?.sorting_group_code || '').trim();
      if (!sgCode) { message.error('未能解析分拣组'); focusOverPickOldCode(); return; }
      const sgId = Number(resp?.data?.sorting_group_id || 0) || undefined;
      const instrId = Number(resp?.data?.instruction_id || 0) || undefined;

      setOverPickGroups(prev => {
        const next = [...(prev || [])];
        // 优先命中“未封存”的同分拣组；若仅命中封存组，则新建一条新记录（临时分拣仓位号递增）
        const idx = next.findIndex(x => String(x?.group_code || '').trim() === sgCode && !(Number((x as any)?.created_container_id || 0) > 0));
        // 兼容：后端可能返回 unit_code 或 system_barcode，统一取 system_barcode 优先
        const iuCode = String(resp?.data?.system_barcode || resp?.data?.unit_code || '').trim();
        const iuId = Number(resp?.data?.inventory_unit_id || 0) || undefined;
        if (idx >= 0) {
          next[idx] = {
            ...next[idx],
            instruction_id: Number(next[idx]?.instruction_id || 0) || instrId,
            items: [...(next[idx].items || []), { old_code: oldCode, qty, inventory_unit_code: iuCode, inventory_unit_id: iuId }],
          };
          try {
            setOverPickTmpLocHint(Number((next[idx] as any)?.temp_loc_no || 0) || (idx + 1));
          } catch {
            setOverPickTmpLocHint(idx + 1);
          }
        } else {
          const nextNo = calcNextTempLocNo(next as any);
          next.push({
            group_code: sgCode,
            group_id: sgId,
            instruction_id: instrId,
            temp_loc_no: nextNo,
            items: [{ old_code: oldCode, qty, inventory_unit_code: iuCode, inventory_unit_id: iuId }],
          });
          setOverPickTmpLocHint(nextNo);
        }
        return next;
      });
      setOverPickOldCode2('');
      setOverPickQty2(1);
      focusOverPickOldCode();
    } catch (e:any) {
      // 重要：无法匹配操作对象IU 等关键提醒，使用居中弹窗，避免顶部 message 被遮挡/看不到
      const detail = String(e?.response?.data?.detail || e?.message || '旧条码解析失败');
      Modal.warning({
        title: '未能匹配操作对象',
        content: detail,
        okText: '知道了',
      });
      focusOverPickOldCode();
    }
  }, [canOperate, overPickQty2, overPickOldCode2, overPickGroups, taskId, instructionId, isBatchMode, focusOverPickOldCode, calcNextTempLocNo]);

  // 步骤2：将分组结构转成 Table 扁平行，并设置 rowSpan 合并前两列
  const overPickFlatRows = useMemo(() => {
    const rows: Array<any> = [];
    (overPickGroups || []).forEach((g, gi) => {
      const items = Array.isArray(g.items) && g.items.length > 0 ? g.items : [{ old_code: '-', qty: 0 }];
      items.forEach((it, ii) => {
        rows.push({
          __groupIndex: gi,
          __itemIndex: ii,
          __rowSpan: items.length,
          tmp_loc_no: Number((g as any)?.temp_loc_no || 0) || (gi + 1),
          sorting_group: g.group_code,
          old_code: String(it?.old_code || '').trim(),
          inventory_unit_code: String(it?.inventory_unit_code || '').trim(),
          inventory_unit_id: it?.inventory_unit_id,
          picked_qty: Math.floor(Number(it?.qty || 0) || 0),
          created_container_id: Number(g?.created_container_id || 0) || 0,
          created_container_code: String(g?.created_container_code || '').trim(),
        });
      });
    });
    return rows;
  }, [overPickGroups]);

  const createExceptionContainer = useCallback(async (args?: { manual_allocations?: Array<{ inventory_unit_id: number; quantity: number }> }) => {
    if (!canOperate) return;
    if (isSingleDropshipStep1Blocked) {
      message.warning('一件代发指令收尾步骤1已禁用');
      return;
    }
    if (isRegularOutboundStep1BlockedByIocTarget) {
      message.warning('常规出库操作对象为历史指令产出容器(IOC)时，收尾步骤1已禁用');
      return;
    }
    if (!instructionId) { message.error('instructionId 缺失，无法生成异常容器'); return; }
    const qtyNum = Number(unoperableQty || 0);
    if (!(qtyNum > 0)) { message.warning('请输入无法操作数量（>0）'); return; }
    const isSortNeed2Or3 = isSortCountInstruction && (sortCountDemandMode === 'need2' || sortCountDemandMode === 'need3');
    const need23Weight = Number(need2ExceptionTotalWeightG || 0);
    if (isSortNeed2Or3 && !(need23Weight > 0)) {
      message.warning(`分拣需求${sortCountDemandMode === 'need3' ? '3' : '2'}请填写本批总重量(g)，且需大于0`);
      return;
    }
    try {
      setUnoperableCreating(true);
      const resp = await request.post('/api/v1/storage/output-containers/exception-create/', {
        task_id: taskId,
        instruction_id: instructionId,
        qty: qtyNum,
        remarks: '无法操作产品：内容物记录为系统按照指令执行结果自动分配的理论值,非确定结果.',
        manual_allocations: args?.manual_allocations || [],
        demand_mode: isSortNeed2Or3 ? sortCountDemandMode : undefined,
        exception_total_weight_g: isSortNeed2Or3 ? need23Weight : undefined,
        execution_session_id: String(executionSessionId || '').trim() || undefined,
      });
      const cont = resp?.data?.container || {};
      const code = String(cont?.code || '').trim().toUpperCase();
      const allocs = Array.isArray(resp?.data?.allocations) ? resp.data.allocations : [];
      const total = Number(resp?.data?.contents_total_qty || 0) || allocs.reduce((s:number, r:any)=> s + Number(r?.quantity||0), 0);
      if (!code) { message.error('生成失败：未返回容器编码'); return; }
      const need2Aiu = resp?.data?.need2_aiu || {};
      const need3Aiu = resp?.data?.need3_aiu || {};
      const aiuInfo = (sortCountDemandMode === 'need3') ? need3Aiu : need2Aiu;
      const allocationSemantic = String(resp?.data?.allocation_semantic || '').trim();
      const finalizeConversionSemantic = String(resp?.data?.finalize_conversion_semantic || '').trim();
      const aiuConversionPending = !!resp?.data?.aiu_conversion_pending;
      const row: ExceptionContainerRow = {
        code,
        total_qty: total,
        allocations: allocs.map((r:any) => ({
          inventory_unit_id: Number(r?.inventory_unit_id || 0) || 0,
          unit_code: String(r?.unit_code || '').trim(),
          quantity: Number(r?.quantity || 0) || 0,
          allocation_semantic: String(r?.allocation_semantic || allocationSemantic || '').trim() || undefined,
          aiu_conversion_pending: (typeof r?.aiu_conversion_pending === 'boolean')
            ? !!r.aiu_conversion_pending
            : aiuConversionPending,
        })).filter((x:any)=> x.inventory_unit_id>0 && x.quantity>0),
        allocation_semantic: allocationSemantic || undefined,
        finalize_conversion_semantic: finalizeConversionSemantic || undefined,
        aiu_conversion_pending: aiuConversionPending,
        demand_mode: String(resp?.data?.demand_mode || '').trim() || undefined,
        exception_total_weight_g: Number(resp?.data?.exception_total_weight_g || 0) || undefined,
        aiu_unit_code: String(aiuInfo?.unit_code || '').trim() || undefined,
        aiu_inventory_unit_id: Number(aiuInfo?.inventory_unit_id || 0) || undefined,
      };
      setExceptionContainers(prev => [row, ...prev]);
      // 同步到“所有容器放入仓位”的列表（无需等待刷新）
      setOutputContainerCodes(prev => {
        const set = new Set((prev || []).map(s => String(s || '').trim().toUpperCase()).filter(Boolean));
        set.add(code);
        const arr = Array.from(set);
        arr.sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
        return arr;
      });
      setUnoperableQty(0);
      if (isSortNeed2Or3) setNeed2ExceptionTotalWeightG(0);
      message.success(`已生成异常容器：${code}`);
      // 自动生成箱标并打开预览（复用换标+装箱箱标样式/内容）
      await openOutputContainerLabelPreview(code, row.allocations);
    } catch (e:any) {
      const data = e?.response?.data;
      if (data && data.need_manual) {
        const remainder = Number(data?.remainder || 0) || 0;
        const cands = Array.isArray(data?.candidates) ? data.candidates : [];
        setManualRemainder(remainder);
        setManualCandidates(cands.map((x:any)=> ({
          inventory_unit_id: Number(x?.inventory_unit_id || 0) || 0,
          unit_code: String(x?.unit_code || '').trim(),
          gap: Number(x?.gap || 0) || 0,
          content_lines: Array.isArray(x?.content_lines) ? x.content_lines.map((s:any) => String(s || '').trim()).filter(Boolean) : [],
        })).filter((x:any)=> x.inventory_unit_id>0));
        setManualInputs({});
        setManualModalOpen(true);
        return;
      }
      message.error(data?.detail || e?.message || '生成异常容器失败');
    } finally {
      setUnoperableCreating(false);
    }
  }, [canOperate, isSingleDropshipStep1Blocked, isRegularOutboundStep1BlockedByIocTarget, instructionId, unoperableQty, isSortCountInstruction, sortCountDemandMode, need2ExceptionTotalWeightG, taskId, setOutputContainerCodes, executionSessionId]);

  // 关闭预览时释放 objectURL
  useEffect(() => {
    return () => {
      try { if (labelPreviewUrl) URL.revokeObjectURL(labelPreviewUrl); } catch {}
    };
  }, [labelPreviewUrl]);

  // load draft
  useEffect(() => {
    const prefix = `WRAPUP_DRAFT_${taskId}_${stageNo}_${stepId}_`;
    const legacyKey = `WRAPUP_DRAFT_${taskId}_${stageNo}_${stepId}`;
    try {
      const keysToDelete: string[] = [];
      for (let i = 0; i < localStorage.length; i += 1) {
        const k = String(localStorage.key(i) || '');
        if (!k) continue;
        if (k === legacyKey) {
          keysToDelete.push(k);
          continue;
        }
        if (k.startsWith(prefix) && k !== draftKey) {
          keysToDelete.push(k);
        }
      }
      keysToDelete.forEach((k) => {
        try { localStorage.removeItem(k); } catch {}
      });
    } catch {}
  }, [draftKey, stageNo, stepId, taskId]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(draftKey);
      if (!raw) return;
      const d = JSON.parse(raw);
      if (d && typeof d === 'object') {
        if (typeof d.remarks === 'string') setRemarks(d.remarks);
        if (Array.isArray(d.put_away_ops)) setPutAwayOps(d.put_away_ops as PutAwayOp[]);
        if (Array.isArray(d.unoperable_ops)) setUnoperableOps(d.unoperable_ops as UnoperableOp[]);
        if (Array.isArray(d.over_pick_ops)) setOverPickOps(d.over_pick_ops as OverPickOp[]);
        if (Array.isArray((d as any).exception_containers)) setExceptionContainers((d as any).exception_containers as any);
        if (Number((d as any)?.need2_exception_total_weight_g || 0) > 0) setNeed2ExceptionTotalWeightG(Number((d as any).need2_exception_total_weight_g || 0));
        // 步骤2：多拣货分拣组（按 sorting_group 批次分组）
        if (Array.isArray((d as any).over_pick_groups)) {
          const rawGroups = (d as any).over_pick_groups as any[];
          // 兼容旧草稿：缺少 temp_loc_no 时按出现顺序补齐（递增）
          let cur = 0;
          const normalized: OverPickGroup[] = rawGroups.map((g: any) => {
            const no = Number(g?.temp_loc_no || 0) || 0;
            if (no > cur) cur = no;
            return {
              group_code: String(g?.group_code || '').trim(),
              group_id: (Number(g?.group_id || 0) || undefined),
              temp_loc_no: no || 0,
              items: Array.isArray(g?.items) ? g.items.map((it: any) => ({
                old_code: String(it?.old_code || '').trim(),
                qty: Math.floor(Number(it?.qty || 0) || 0),
                inventory_unit_code: String(it?.inventory_unit_code || '').trim(),
                inventory_unit_id: (Number(it?.inventory_unit_id || 0) || undefined),
              })) : [],
              created_container_id: (Number(g?.created_container_id || 0) || undefined),
              created_container_code: String(g?.created_container_code || '').trim() || undefined,
            };
          });
          // 补齐缺失 temp_loc_no
          const filled = normalized.map((g) => {
            if (Number(g.temp_loc_no || 0) > 0) return g;
            cur += 1;
            return { ...g, temp_loc_no: cur };
          }).filter(g => String(g.group_code || '').trim());
          setOverPickGroups(filled);
        }
      }
    } catch {}
  }, [draftKey]);

  // 从草稿 put_away_ops 还原 map（用于“已放入仓位”统计）
  useEffect(() => {
    try {
      const map: Record<string, string> = {};
      (putAwayOps || []).forEach((r:any) => {
        const c = String(r?.container_code || '').trim().toUpperCase();
        const loc = String(r?.location_code || '').trim().toUpperCase();
        if (c && loc) map[c] = loc;
      });
      // 仅当当前 map 为空时才回填，避免覆盖用户实时扫描
      if (Object.keys(putAwayLocByContainer || {}).length === 0 && Object.keys(map).length > 0) {
        setPutAwayLocByContainer(map);
      }
    } catch {}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [putAwayOps]);

  const doSaveDraft = useCallback(async () => {
    try {
      setSaving(true);
      const draft = {
        put_away_ops: putAwayOps,
        unoperable_ops: unoperableOps,
        over_pick_ops: overPickOps,
        over_pick_groups: overPickGroups,
        remarks,
        exception_containers: exceptionContainers,
        need2_exception_total_weight_g: need2ExceptionTotalWeightG,
        version: 4,
        updated_at: Date.now(),
      };
      localStorage.setItem(draftKey, JSON.stringify(draft));
      message.success('已保存草稿');
    } catch {
      message.error('保存草稿失败');
    } finally {
      setSaving(false);
    }
  }, [draftKey, putAwayOps, unoperableOps, overPickOps, overPickGroups, remarks, exceptionContainers, need2ExceptionTotalWeightG]);

  // debounce auto-save
  useEffect(() => {
    if (!canOperate) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      try {
        const draft = {
          put_away_ops: putAwayOps,
          unoperable_ops: unoperableOps,
          over_pick_ops: overPickOps,
          over_pick_groups: overPickGroups,
          remarks,
          exception_containers: exceptionContainers,
          need2_exception_total_weight_g: need2ExceptionTotalWeightG,
          version: 4,
          updated_at: Date.now(),
        };
        localStorage.setItem(draftKey, JSON.stringify(draft));
      } catch {}
    }, 1000);
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
  }, [putAwayOps, unoperableOps, overPickOps, overPickGroups, remarks, exceptionContainers, need2ExceptionTotalWeightG, draftKey, canOperate]);

  const metrics = useMemo(() => ({
    put_away_ops: putAwayOps,
    unoperable_ops: unoperableOps,
    over_pick_ops: overPickOps,
    remarks,
  }), [putAwayOps, unoperableOps, overPickOps, remarks]);

  // 将收尾关键数据同步到父级 metricsDraft（用于“提交记录并完成”一键落库，不依赖单独提交按钮）
  useEffect(() => {
    try { onChangeRef.current?.('remarks', remarks); } catch {}
  }, [remarks]);
  useEffect(() => {
    try { onChangeRef.current?.('put_away_ops', putAwayOps); } catch {}
  }, [putAwayOps]);
  useEffect(() => {
    try { onChangeRef.current?.('unoperable_ops', unoperableOps); } catch {}
  }, [unoperableOps]);
  useEffect(() => {
    try { onChangeRef.current?.('over_pick_ops', overPickOps); } catch {}
  }, [overPickOps]);
  useEffect(() => {
    try { onChangeRef.current?.('over_pick_groups', overPickGroups); } catch {}
  }, [overPickGroups]);
  useEffect(() => {
    try { onChangeRef.current?.('exception_containers', exceptionContainers); } catch {}
  }, [exceptionContainers]);
  useEffect(() => {
    try { onChangeRef.current?.('need2_exception_total_weight_g', need2ExceptionTotalWeightG); } catch {}
  }, [need2ExceptionTotalWeightG]);
  useEffect(() => {
    try { onChangeRef.current?.('put_away_gate', putAwayGate); } catch {}
  }, [putAwayGate]);

  const handleSubmit = useCallback(async () => {
    if (!canOperate) return;
    try {
      setSubmitting(true);
      if (onSubmit) {
        await onSubmit(metrics);
      } else {
        message.error('未接入提交流程');
      }
      // 成功后清除草稿
      try { localStorage.removeItem(draftKey); } catch {}
    } catch (e: any) {
      message.error(e?.message || '提交失败');
    } finally {
      setSubmitting(false);
    }
  }, [canOperate, onSubmit, metrics, draftKey]);

  const putAwayCols = useMemo(() => [
    { title: '产出容器', dataIndex: 'container_code', key: 'container_code', render: (_: any, r: PutAwayOp, idx: number) => (
      <Input
        disabled={!canOperate}
        value={r.container_code}
        onChange={(e)=> setPutAwayOps(prev => prev.map((x,i)=> i===idx ? { ...x, container_code: String(e.target.value || '').toUpperCase().trim() } : x))}
        placeholder="容器编码"
      />
    ) },
    { title: '放入仓位', dataIndex: 'location_code', key: 'location_code', render: (_: any, r: PutAwayOp, idx: number) => (
      <Input
        disabled={!canOperate}
        value={r.location_code}
        onChange={(e)=> setPutAwayOps(prev => prev.map((x,i)=> i===idx ? { ...x, location_code: String(e.target.value || '').toUpperCase().trim() } : x))}
        placeholder="仓位编码"
      />
    ) },
    { title: '操作', key: 'ops', width: 80, render: (_: any, __: any, idx: number) => (
      <Button danger size="small" disabled={!canOperate} onClick={() => setPutAwayOps(prev => prev.filter((_, i) => i !== idx))}>删除</Button>
    ) }
  ], [putAwayOps, canOperate]);

  const unoperableCols = useMemo(() => [
    { title: '库存单元', dataIndex: 'unit_code', key: 'unit', width: 220, render: (_: any, r: UnoperableOp, idx: number) => (
      <Input disabled={!canOperate} value={r.unit_code} onChange={(e)=> setUnoperableOps(prev => prev.map((x,i)=> i===idx ? { ...x, unit_code: String(e.target.value || '').toUpperCase().trim() } : x))} />
    ) },
    { title: '数量', dataIndex: 'qty', key: 'qty', width: 110, render: (_: any, r: UnoperableOp, idx: number) => (
      <Input disabled={!canOperate} type="number" value={r.qty as any} onChange={(e)=> setUnoperableOps(prev => prev.map((x,i)=> i===idx ? { ...x, qty: Number(e.target.value || 0) } : x))} />
    ) },
    { title: '原因', dataIndex: 'reason', key: 'reason', render: (_: any, r: UnoperableOp, idx: number) => (
      <Input disabled={!canOperate} value={r.reason} onChange={(e)=> setUnoperableOps(prev => prev.map((x,i)=> i===idx ? { ...x, reason: String(e.target.value || '') } : x))} />
    ) },
    { title: '生成容器', dataIndex: 'new_container', key: 'new_container', width: 160, render: (_: any, r: UnoperableOp, idx: number) => (
      <Input disabled={!canOperate} value={r.new_container} onChange={(e)=> setUnoperableOps(prev => prev.map((x,i)=> i===idx ? { ...x, new_container: String(e.target.value || '').toUpperCase().trim() } : x))} placeholder="新容器编码" />
    ) },
    { title: '放入仓位', dataIndex: 'location_code', key: 'loc', width: 140, render: (_: any, r: UnoperableOp, idx: number) => (
      <Input disabled={!canOperate} value={r.location_code} onChange={(e)=> setUnoperableOps(prev => prev.map((x,i)=> i===idx ? { ...x, location_code: String(e.target.value || '').toUpperCase().trim() } : x))} placeholder="仓位编码" />
    ) },
    { title: '操作', key: 'ops', width: 80, render: (_: any, __: any, idx: number) => (
      <Button danger size="small" disabled={!canOperate} onClick={() => setUnoperableOps(prev => prev.filter((_, i) => i !== idx))}>删除</Button>
    ) }
  ], [unoperableOps, canOperate]);

  const overPickCols = useMemo(() => [
    { title: '库存单元', dataIndex: 'unit_code', key: 'unit', width: 220, render: (_: any, r: OverPickOp, idx: number) => (
      <Input disabled={!canOperate} value={r.unit_code} onChange={(e)=> setOverPickOps(prev => prev.map((x,i)=> i===idx ? { ...x, unit_code: String(e.target.value || '').toUpperCase().trim() } : x))} />
    ) },
    { title: '数量', dataIndex: 'qty', key: 'qty', width: 110, render: (_: any, r: OverPickOp, idx: number) => (
      <Input disabled={!canOperate} type="number" value={r.qty as any} onChange={(e)=> setOverPickOps(prev => prev.map((x,i)=> i===idx ? { ...x, qty: Number(e.target.value || 0) } : x))} />
    ) },
    { title: '生成容器', dataIndex: 'new_container', key: 'new_container', width: 160, render: (_: any, r: OverPickOp, idx: number) => (
      <Input disabled={!canOperate} value={r.new_container} onChange={(e)=> setOverPickOps(prev => prev.map((x,i)=> i===idx ? { ...x, new_container: String(e.target.value || '').toUpperCase().trim() } : x))} placeholder="新容器编码" />
    ) },
    { title: '放入仓位', dataIndex: 'location_code', key: 'loc', width: 140, render: (_: any, r: OverPickOp, idx: number) => (
      <Input disabled={!canOperate} value={r.location_code} onChange={(e)=> setOverPickOps(prev => prev.map((x,i)=> i===idx ? { ...x, location_code: String(e.target.value || '').toUpperCase().trim() } : x))} placeholder="仓位编码" />
    ) },
    { title: '操作', key: 'ops', width: 80, render: (_: any, __: any, idx: number) => (
      <Button danger size="small" disabled={!canOperate} onClick={() => setOverPickOps(prev => prev.filter((_, i) => i !== idx))}>删除</Button>
    ) }
  ], [overPickOps, canOperate]);

  return (
    <div style={{ width: '100%' }}>
      <style>{`
        .wrapup-grid {
          display: grid;
          /* 关键：使用 minmax(0, 1fr) 允许网格项在内容过宽时正确收缩 */
          grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
          gap: 12px;
          background: #fafafa;
          border: 1px solid #d9d9d9;
          border-radius: 8px;
          padding: 10px;
        }
        /* 防止网格子项最小内容宽度把整列撑爆 */
        .wrapup-grid > * {
          min-width: 0;
        }
        /* 表格等宽内容在卡片内滚动，不再把卡片/外层容器撑出边界 */
        .wrapup-grid .ant-table-wrapper {
          max-width: 100%;
          overflow-x: auto;
        }
        @media (max-width: 980px) {
          .wrapup-grid { grid-template-columns: 1fr; }
        }
        /* 产出容器列表：两列网格（更紧凑） */
        .wrapup-oc-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 8px 12px;
        }
        .wrapup-oc-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
          border: 1px solid #f0f0f0;
          border-radius: 6px;
          padding: 6px 8px;
          background: #fff;
        }
        .wrapup-oc-item.done {
          background: #f5f5f5;
          border-color: #d9d9d9;
        }
        .wrapup-oc-item .mono { font-family: monospace; color: #595959; }
        .wrapup-oc-item.done .mono,
        .wrapup-oc-item.done .loc { color: #8c8c8c; }
        .wrapup-oc-item .loc { font-family: monospace; color: #595959; }
        .wrapup-oc-item .loc.empty { color: #bfbfbf; }
      `}</style>

      <div className="wrapup-grid">
        {isSortCountInstruction ? (
          <Card
            size="small"
            headStyle={{ background:'#fff7e6', borderBottom: '1px solid #ffd591' }}
            title={(
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                <span>步骤1:分拣出的无法处理产品生成指令产出容器IOC</span>
                <HelpHintButton onClick={() => setUnoperableHelpOpen(true)}>说明</HelpHintButton>
              </div>
            )}
          >
            <div style={{ display:'flex', alignItems:'center', gap: 10, marginBottom: 10, flexWrap:'wrap' }}>
              <span style={{ color:'#595959' }}>数量</span>
              <InputNumber
                min={1}
                precision={0}
                value={unoperableQty}
                disabled={!canOperate || isBatchMode}
                onChange={(v)=> setUnoperableQty(Number(v || 0))}
                style={{ width: 160 }}
                placeholder="输入数量"
              />
              {(sortCountDemandMode === 'need2' || sortCountDemandMode === 'need3') ? (
                <>
                  <span style={{ color:'#595959' }}>总重量(g)</span>
                  <InputNumber
                    min={0}
                    precision={0}
                    value={need2ExceptionTotalWeightG}
                    disabled={!canOperate || isBatchMode}
                    onChange={(v)=> setNeed2ExceptionTotalWeightG(Number(v || 0))}
                    style={{ width: 180 }}
                    placeholder="输入本批总重量"
                  />
                </>
              ) : null}
              <Button
                type="primary"
                disabled={!canOperate || isBatchMode || !(Number(unoperableQty || 0) > 0) || ((sortCountDemandMode === 'need2' || sortCountDemandMode === 'need3') && !(Number(need2ExceptionTotalWeightG || 0) > 0))}
                loading={unoperableCreating}
                onClick={() => { createExceptionContainer(); }}
              >
                生成容器
              </Button>
              <span style={{ color: '#1677ff', fontWeight: 600 }}>{sortCountDemandText}</span>
              {isBatchMode ? (
                <span style={{ color: '#8c8c8c' }}>批次模式已禁用步骤1（无法操作产品生成容器）</span>
              ) : null}
            </div>

            <Table
              size="small"
              rowKey={(r:any) => String(r?.code || '')}
              pagination={false}
              dataSource={exceptionContainers as any}
              columns={[
                { title: '序号', key: '__idx', width: 70, align: 'center' as any, render: (_:any, __:any, idx:number) => idx + 1 },
                { title: '容器系统编码', dataIndex: 'code', key: 'code', width: 200, render: (v:any) => (
                  <Typography.Text copyable={{ text: String(v || '') }} style={{ fontFamily:'monospace', color:'#595959' }}>
                    {String(v || '-')}
                  </Typography.Text>
                )},
                { title: '内容物总数量', dataIndex: 'total_qty', key: 'total_qty', width: 120, align: 'center' as any, render: (v:any) => Number(v || 0) },
                { title: '来源IU事实分配', dataIndex: 'allocations', key: 'allocations', render: (_:any, r:any) => {
                  const allocs: ExceptionAllocRow[] = Array.isArray(r?.allocations) ? r.allocations : [];
                  const top = allocs.slice(0, 3);
                  const extra = Math.max(allocs.length - 3, 0);
                  const parts = top.map((a) => `${String(a?.unit_code || '').trim()}×${Number(a?.quantity || 0)}件`).filter(Boolean);
                  return (
                    <span style={{ fontFamily:'monospace', display: 'inline-block' }}>
                      {parts.length > 0 ? (
                        parts.map((p, idx) => <div key={`alloc-row-${idx}`}>{p}</div>)
                      ) : '-'}
                      {extra > 0 ? (
                        <div>
                          <a onClick={() => openAllocationsModal(String(r?.code || ''), allocs)}>+{extra}条</a>
                        </div>
                      ) : null}
                    </span>
                  );
                }},
                { title: '操作', key: 'ops', width: 120, align: 'center' as any, render: (_:any, r:any) => {
                  const code = String(r?.code || '').trim().toUpperCase();
                  const allocs: ExceptionAllocRow[] = Array.isArray(r?.allocations) ? r.allocations : [];
                  return (
                    <Button
                      size="small"
                      onClick={() => openOutputContainerLabelPreview(code, allocs)}
                      disabled={!code}
                    >
                      查看箱标
                    </Button>
                  );
                }},
              ] as any}
              locale={{ emptyText: '暂无记录' }}
            />
            <div style={{ marginTop: 6, color: '#8c8c8c', fontSize: 12 }}>
              {(sortCountDemandMode === 'need2' || sortCountDemandMode === 'need3')
                ? '步骤1展示的是AIU异常承接事实（含数量/总重量）；终审阶段统一执行库存结算。'
                : '步骤1展示的是来源IU事实分配；需要转换为AIU的场景将在终审库存结算中统一执行。'}
            </div>
          </Card>
        ) : (
          <Card
            size="small"
            headStyle={{ background:'#fff7e6', borderBottom: '1px solid #ffd591' }}
            title={(
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                <span>步骤1:无法处理的操作对象生成指令产出容器IOC</span>
                <HelpHintButton onClick={() => setUnoperableHelpOpen(true)}>说明</HelpHintButton>
              </div>
            )}
          >
            <div style={{ display:'flex', alignItems:'center', gap: 10, marginBottom: 10, flexWrap:'wrap' }}>
              <span style={{ color:'#595959' }}>数量</span>
              <InputNumber
                min={1}
                precision={0}
                value={unoperableQty}
                disabled={!canOperate || isBatchMode || isSingleDropshipStep1Blocked || isRegularOutboundStep1BlockedByIocTarget}
                onChange={(v)=> setUnoperableQty(Number(v || 0))}
                style={{ width: 160 }}
                placeholder="输入数量"
              />
              <Button
                type="primary"
                disabled={!canOperate || isBatchMode || isSingleDropshipStep1Blocked || isRegularOutboundStep1BlockedByIocTarget || !(Number(unoperableQty || 0) > 0)}
                loading={unoperableCreating}
                onClick={() => createExceptionContainer()}
              >
                生成容器
              </Button>
              {isBatchMode ? (
                <span style={{ color: '#8c8c8c' }}>批次模式已禁用步骤1（无法操作产品生成容器）</span>
              ) : null}
              {isRegularOutboundStep1BlockedByIocTarget ? (
                <span style={{ color: '#cf1322' }}>常规出库操作对象为历史指令产出容器(IOC)时，步骤1已禁用</span>
              ) : null}
              {isSingleDropshipStep1Blocked ? (
                <span style={{ color: '#cf1322' }}>一件代发指令收尾步骤1已禁用</span>
              ) : null}
              {isRegularOutboundInstruction && !isRegularOutboundStep1BlockedByIocTarget && regularOutboundStep1Ctx.loaded ? (
                <span style={{ color: '#8c8c8c' }}>
                  常规出库（当前库存操作对象）步骤1口径：单一源IU自动绑定，多源IU需手动分配
                </span>
              ) : null}
            </div>

            <Table
              size="small"
              rowKey={(r:any) => String(r?.code || '')}
              pagination={false}
              dataSource={exceptionContainers as any}
              columns={[
                { title: '序号', key: '__idx', width: 70, align: 'center' as any, render: (_:any, __:any, idx:number) => idx + 1 },
                { title: '容器系统编码', dataIndex: 'code', key: 'code', width: 200, render: (v:any) => (
                  <Typography.Text copyable={{ text: String(v || '') }} style={{ fontFamily:'monospace', color:'#595959' }}>
                    {String(v || '-')}
                  </Typography.Text>
                )},
                { title: '内容物总数量', dataIndex: 'total_qty', key: 'total_qty', width: 120, align: 'center' as any, render: (v:any) => Number(v || 0) },
                { title: '来源IU事实分配', dataIndex: 'allocations', key: 'allocations', render: (_:any, r:any) => {
                  const allocs: ExceptionAllocRow[] = Array.isArray(r?.allocations) ? r.allocations : [];
                  const top = allocs.slice(0, 3);
                  const extra = Math.max(allocs.length - 3, 0);
                  const parts = top.map((a) => `${String(a?.unit_code || '').trim()}×${Number(a?.quantity || 0)}件`).filter(Boolean);
                  return (
                    <span style={{ fontFamily:'monospace', display: 'inline-block' }}>
                      {parts.length > 0 ? (
                        parts.map((p, idx) => <div key={`alloc-row2-${idx}`}>{p}</div>)
                      ) : '-'}
                      {extra > 0 ? (
                        <div>
                          <a onClick={() => openAllocationsModal(String(r?.code || ''), allocs)}>+{extra}条</a>
                        </div>
                      ) : null}
                    </span>
                  );
                }},
                { title: '操作', key: 'ops', width: 120, align: 'center' as any, render: (_:any, r:any) => {
                  const code = String(r?.code || '').trim().toUpperCase();
                  const allocs: ExceptionAllocRow[] = Array.isArray(r?.allocations) ? r.allocations : [];
                  return (
                    <Button
                      size="small"
                      onClick={() => openOutputContainerLabelPreview(code, allocs)}
                      disabled={!code}
                    >
                      查看箱标
                    </Button>
                  );
                }},
              ] as any}
              locale={{ emptyText: '暂无记录' }}
            />
            <div style={{ marginTop: 6, color: '#8c8c8c', fontSize: 12 }}>
              步骤1展示的是来源IU事实分配；需要转换为AIU的场景将在终审库存结算中统一执行。
            </div>
          </Card>
        )}

        <Card size="small" title="步骤2: 多拣货操作对象生成仓储容器SC" headStyle={{ background:'#f9f0ff', borderBottom: '1px solid #d3adf7' }}>
          <style>{`
            /* 步骤2：已生成容器的分拣组整行置灰（封存/禁用） */
            .overpick-row-locked > td {
              background: #f5f5f5 !important;
              color: #8c8c8c !important;
            }
            .overpick-row-locked a,
            .overpick-row-locked .ant-typography {
              color: #8c8c8c !important;
            }
          `}</style>
          <div style={{ display:'flex', alignItems:'center', gap: 10, marginBottom: 10, flexWrap:'wrap' }}>
            <span style={{ color:'#595959' }}>数量</span>
            <InputNumber
              min={1}
              precision={0}
              value={overPickQty2}
              disabled={!canOperate}
              onChange={(v)=> setOverPickQty2(Number(v || 0))}
              style={{ width: 80 }}
              placeholder="输入数量"
            />
            <span style={{ color:'#595959' }}>扫描标识</span>
            <Input
              ref={overPickOldCodeRef}
              value={overPickOldCode2}
              disabled={!canOperate}
              onChange={(e)=> setOverPickOldCode2(e.target.value)}
              placeholder="扫描散货类产品条码或BIU/PIU系统编码"
              style={{ width: 200 }}
              onPressEnter={() => {
                submitOverPickSort();
              }}
            />
            <Button
              type="primary"
              disabled={!canOperate}
              onClick={() => {
                submitOverPickSort();
              }}
            >
              分拣
            </Button>
            {overPickTmpLocHint != null ? (
              <>
                <span style={{ color:'#595959' }}>临时分拣仓位:</span>
                <span
                  title="本次分拣命中的临时分拣仓位"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '0 12px',
                    height: 36,
                    borderRadius: 10,
                    background: '#fff566',
                    border: '1px solid #faad14',
                    color: '#cf1322',
                    fontWeight: 900,
                    fontSize: 20,
                    lineHeight: '36px',
                    boxShadow: '0 1px 0 rgba(0,0,0,0.06)',
                  }}
                >
                  {overPickTmpLocHint}
                </span>
              </>
            ) : null}
          </div>

          <Table
            size="small"
            rowKey={(r:any) => `g${r.__groupIndex}-i${r.__itemIndex}`}
            pagination={false}
            dataSource={overPickFlatRows as any}
            rowClassName={(r:any) => (Number(r?.created_container_id || 0) > 0 ? 'overpick-row-locked' : '')}
            columns={[
              {
                title: '临时分拣仓位',
                key: 'tmp_loc',
                width: 120,
                align: 'center' as any,
                render: (_:any, r:any) => {
                  const obj: any = { children: r.tmp_loc_no, props: {} };
                  obj.props.rowSpan = (r.__itemIndex === 0) ? r.__rowSpan : 0;
                  return obj;
                },
              },
              {
                title: '分拣组',
                dataIndex: 'sorting_group',
                key: 'group',
                width: 120,
                align: 'center' as any,
                render: (v:any, r:any) => {
                  const locked = Number(r?.created_container_id || 0) > 0;
                  const obj: any = {
                    children: (
                      <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap: 4 }}>
                        <span>{String(v || '-') || '-'}</span>
                        {locked ? <span style={{ fontSize: 12, color:'#8c8c8c' }}>已生成/禁用</span> : null}
                      </div>
                    ),
                    props: {}
                  };
                  obj.props.rowSpan = (r.__itemIndex === 0) ? r.__rowSpan : 0;
                  return obj;
                },
              },
              {
                title: '库存单元',
                dataIndex: 'inventory_unit_code',
                key: 'inventory_unit_code',
                width: 160,
                align: 'center' as any,
                render: (v:any) => (
                  <Typography.Text copyable={{ text: String(v || '') }} style={{ fontFamily:'monospace', color:'#595959' }}>
                    {String(v || '-')}
                  </Typography.Text>
                ),
              },
              {
                title: '扫描值',
                dataIndex: 'old_code',
                key: 'old_code',
                width: 180,
                ellipsis: true,
                render: (v:any) => (
                  <Typography.Text copyable={{ text: String(v || '') }} style={{ fontFamily:'monospace', color:'#595959' }}>
                    {String(v || '-')}
                  </Typography.Text>
                ),
              },
              {
                title: '已分拣数量',
                dataIndex: 'picked_qty',
                key: 'picked_qty',
                width: 90,
                align: 'center' as any,
                render: (v:any) => Number(v || 0),
              },
              {
                title: '操作',
                key: 'ops',
                width: 240,
                align: 'center' as any,
                render: (_:any, r:any) => {
                  // 与“临时分拣仓位/分拣组”一致：按组纵向合并，只在组首行展示按钮
                  const obj: any = { children: null, props: {} };
                  obj.props.rowSpan = (r.__itemIndex === 0) ? r.__rowSpan : 0;
                  if (r.__itemIndex !== 0) return obj;
                  const gi = Number(r.__groupIndex || 0);
                  const g = overPickGroups?.[gi];
                  const createdId = Number(g?.created_container_id || 0) || 0;
                  const hasCreated = createdId > 0;
                  obj.children = (
                    <Space size="small" style={{ whiteSpace: 'nowrap' }}>
                      <Button
                        size="small"
                        disabled={!canOperate || hasCreated}
                        onClick={() => {
                          (async () => {
                            setOverPickCreateGroupIndex(gi);
                            await ensureStorageContainerTypeChoices();
                            setOverPickCreateOpen(true);
                          })();
                        }}
                      >
                        生成容器
                      </Button>
                      <Button
                        size="small"
                        disabled={!canOperate || !hasCreated}
                        onClick={async () => {
                          if (!hasCreated) return;
                          await openStorageContainerLabelPreview(createdId);
                          await printStorageContainerLabel(createdId);
                          focusOverPickOldCode();
                        }}
                      >
                        查看箱标
                      </Button>
                    </Space>
                  );
                  return obj;
                },
              }
            ] as any}
            locale={{ emptyText: '暂无分拣记录' }}
          />
        </Card>

        <Card
          size="small"
          headStyle={{ background: '#e6f4ff', borderBottom: '1px solid #91caff' }}
          title={(
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
              <span>
                步骤3: 所有容器放入仓位
                <span style={{ color:'#8c8c8c', fontSize: 12, marginLeft: 8 }}>
                  （已消耗/已作废容器不参与放入仓位）
                </span>
              </span>
              <span style={{ color:'#595959', fontWeight: 500 }}>
                共有 <b>{putAwayStats.total}</b> 个容器　已放入仓位 <b>{putAwayStats.done}</b> 个　未放入仓位 <b>{putAwayStats.todo}</b> 个
              </span>
            </div>
          )}
        >
          {/* 扫描区：上下两行、同宽 */}
          <div style={{ display:'flex', flexDirection:'column', gap: 8, marginBottom: 10 }}>
            <div style={{ display:'flex', alignItems:'center', gap: 8 }}>
              <span style={{ width: 84, color:'#595959', whiteSpace:'nowrap' }}>扫描容器</span>
              <Input
                ref={scanOutRef}
                disabled={!canOperate}
                value={scanOutContainer}
                placeholder={(putAwayActiveTab === 'sc' || isOperationTabStorage) ? '扫描仓储容器条码（SC-...）' : '扫描产出容器箱标条码（IOC-...）'}
                style={{ width: 320 }}
                onFocus={() => { try { scanOutRef.current?.select?.(); } catch {} }}
                onChange={(e)=> setScanOutContainer(e.target.value)}
                onPressEnter={() => {
                  addScannedContainer(scanOutContainer);
                }}
              />
              <Button
                disabled={!canOperate}
                onClick={() => {
                  addScannedContainer(scanOutContainer);
                }}
              >
                输入
              </Button>
              <span style={{ color:'#595959', whiteSpace:'nowrap' }}>
                已选定 <b>{(selectedScanned || []).length}</b> 个容器
              </span>
            </div>
            <div style={{ display:'flex', alignItems:'center', gap: 8 }}>
              <span style={{ width: 84, color:'#595959', whiteSpace:'nowrap' }}>扫描仓位</span>
              <Input
                ref={scanLocRef}
                disabled={!canOperate}
                value={scanLocation}
                placeholder={(putAwayActiveTab === 'sc' || isOperationTabStorage) ? '必须是系统仓位编码（需存在于数据库）' : '可留空或手动输入文本'}
                style={{ width: 320 }}
                onChange={(e)=> setScanLocation(e.target.value)}
                onPressEnter={() => {
                  applyLocationToSelected(scanLocation);
                }}
              />
              <Button
                disabled={!canOperate}
                onClick={() => {
                  applyLocationToSelected(scanLocation);
                }}
              >
                输入
              </Button>
            </div>
          </div>

          {(() => {
            const selectedSet = new Set((selectedScanned || []).map((k) => String(k || '').trim().toUpperCase()).filter(Boolean));
            const iocException = iocExceptionCodes;
            const iocNormal = operationGeneratedCodes;

            const countDone = (codes: string[]) => {
              try {
                return (codes || []).reduce((s, code) => {
                  const c = String(code || '').trim().toUpperCase();
                  const loc = String(putAwayLocByContainer?.[c] || '').trim().toUpperCase();
                  return s + (loc ? 1 : 0);
                }, 0);
              } catch {
                return 0;
              }
            };
            const normalDone = countDone(iocNormal);
            const excDone = countDone(iocException);
            const scDone = countDone(scCodes);

            const renderOneCard = (args: {
              title: string;
              codes: string[];
              selectable: boolean;
              doneCount?: number;
              extraHint?: string;
              keyPrefix: string;
            }) => {
              const codes = args.codes || [];
              const selectedCount = args.selectable
                ? codes.reduce((s, c0) => s + (selectedSet.has(String(c0 || '').trim().toUpperCase()) ? 1 : 0), 0)
                : 0;
              const allChecked = args.selectable && codes.length > 0 && selectedCount === codes.length;
              const ind = args.selectable && selectedCount > 0 && selectedCount < codes.length;
              return (
                <div>
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom: 8 }}>
                    <div style={{ display:'flex', alignItems:'center', gap: 10 }}>
                      <Checkbox
                        indeterminate={ind}
                        checked={allChecked}
                        disabled={!canOperate || !args.selectable}
                        onChange={(e) => {
                          const checked = !!e.target.checked;
                          if (!args.selectable) return;
                          // 需求：全选仅作用于本类容器；切换卡片页后再次全选时，清空之前选择，仅保留当前类
                          if (checked) setSelectedScanned(codes.map((c0) => String(c0 || '').trim().toUpperCase()).filter(Boolean));
                          else setSelectedScanned([]);
                        }}
                      >
                        全选
                      </Checkbox>
                      <div style={{ color:'#595959' }}>已选：<b>{selectedCount}</b></div>
                    </div>
                    <div style={{ color:'#595959' }}>
                      共 <b>{codes.length}</b>
                      {typeof args.doneCount === 'number'
                        ? <>　已放入 <b>{args.doneCount}</b>　未放入 <b>{Math.max(codes.length - args.doneCount, 0)}</b></>
                        : null}
                    </div>
                  </div>
                  {args.extraHint ? (
                    <div style={{ marginBottom: 8, color:'#8c8c8c' }}>{args.extraHint}</div>
                  ) : null}
                  <div className="wrapup-oc-grid">
                    {codes.map((code) => {
                      const c = String(code || '').trim().toUpperCase();
                      const loc = String(putAwayLocByContainer?.[c] || '').trim().toUpperCase();
                      const done = !!loc;
                      const checked = selectedSet.has(c);
                      const disabled = (!canOperate) || (!args.selectable);
                      return (
                        <div key={`${args.keyPrefix}-${c}`} className={`wrapup-oc-item ${done ? 'done' : ''}`} style={!args.selectable ? { background:'#fafafa', color:'#8c8c8c' } : undefined}>
                          <div style={{ display:'flex', alignItems:'center', gap: 8, minWidth: 0 }}>
                            <Checkbox
                              checked={checked && args.selectable}
                              disabled={disabled}
                              onChange={(e) => {
                                if (!args.selectable) return;
                                const ck = !!e.target.checked;
                                setSelectedScanned(prev => {
                                  const set = new Set((prev || []).map(x => String(x || '').trim().toUpperCase()).filter(Boolean));
                                  if (ck) set.add(c);
                                  else set.delete(c);
                                  return Array.from(set);
                                });
                                focusContainerInput();
                              }}
                            />
                            <Typography.Text className="mono" style={{ maxWidth: 220, color: (!args.selectable ? '#8c8c8c' : undefined) }} ellipsis copyable={{ text: c }}>
                              {c}
                            </Typography.Text>
                          </div>
                          <div className={`loc ${loc ? '' : 'empty'}`} style={{ flexShrink: 0, minWidth: 90, textAlign:'right', color: (!args.selectable ? '#8c8c8c' : undefined) }}>
                            {args.selectable ? (loc || '-') : '-'}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            };
            const renderIocNormalCard = () => {
              if (isSortCountInstruction) {
                return renderOneCard({
                  title: '操作过程生成的容器（仓储容器）',
                  codes: iocNormal,
                  selectable: true,
                  doneCount: normalDone,
                  keyPrefix: 'op-sc',
                });
              }
              const groups = Array.isArray(iocNormalBundleMeta.groupedBundles) ? iocNormalBundleMeta.groupedBundles : [];
              const standalone = Array.isArray(iocNormalBundleMeta.standaloneCodes) ? iocNormalBundleMeta.standaloneCodes : [];
              const selectedCount = iocNormal.reduce((s, c0) => s + (selectedSet.has(String(c0 || '').trim().toUpperCase()) ? 1 : 0), 0);
              const allChecked = iocNormal.length > 0 && selectedCount === iocNormal.length;
              const ind = selectedCount > 0 && selectedCount < iocNormal.length;
              return (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <Checkbox
                        indeterminate={ind}
                        checked={allChecked}
                        disabled={!canOperate}
                        onChange={(e) => {
                          const checked = !!e.target.checked;
                          if (checked) setSelectedScanned(iocNormal.map((c0) => String(c0 || '').trim().toUpperCase()).filter(Boolean));
                          else setSelectedScanned([]);
                        }}
                      >
                        全选
                      </Checkbox>
                      <div style={{ color: '#595959' }}>已选：<b>{selectedCount}</b></div>
                    </div>
                    <div style={{ color: '#595959' }}>
                      共 <b>{iocNormal.length}</b>　已放入 <b>{normalDone}</b>　未放入 <b>{Math.max(iocNormal.length - normalDone, 0)}</b>
                    </div>
                  </div>

                  {groups.length > 0 ? (
                    <div style={{ marginBottom: 12, border: '1px solid #b4b5ff', borderRadius: 8, background: '#f7f7ff', padding: 10 }}>
                      <div style={{ fontWeight: 600, color: '#3f3d99', marginBottom: 8 }}>父-子组合容器</div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {groups.map((g, idx) => {
                          const parentCode = String(g?.parentCode || '').trim().toUpperCase();
                          const childCodes = Array.isArray(g?.childCodes) ? g.childCodes : [];
                          const allCodes = Array.isArray(g?.allCodes) ? g.allCodes : [parentCode];
                          const selectedInGroup = allCodes.reduce((s, c0) => s + (selectedSet.has(String(c0 || '').trim().toUpperCase()) ? 1 : 0), 0);
                          const groupChecked = allCodes.length > 0 && selectedInGroup === allCodes.length;
                          const groupInd = selectedInGroup > 0 && selectedInGroup < allCodes.length;
                          const doneInGroup = allCodes.reduce((s, c0) => {
                            const loc = String(putAwayLocByContainer?.[String(c0 || '').trim().toUpperCase()] || '').trim().toUpperCase();
                            return s + (loc ? 1 : 0);
                          }, 0);
                          return (
                            <div
                              key={`ioc-normal-group-${parentCode || idx}`}
                              className={`wrapup-oc-item ${doneInGroup === allCodes.length && allCodes.length > 0 ? 'done' : ''}`}
                              style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 8 }}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                                  <Checkbox
                                    checked={groupChecked}
                                    indeterminate={groupInd}
                                    disabled={!canOperate}
                                    onChange={(e) => {
                                      toggleIocNormalBundleSelection(parentCode, !!e.target.checked);
                                      focusContainerInput();
                                    }}
                                  />
                                  <Tag color="blue" style={{ marginInlineEnd: 0 }}>父容器</Tag>
                                  <Typography.Text className="mono" style={{ maxWidth: 260 }} ellipsis copyable={{ text: parentCode }}>
                                    {parentCode || '-'}
                                  </Typography.Text>
                                </div>
                              </div>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, paddingLeft: 28 }}>
                                {childCodes.map((cc, cidx) => {
                                  const child = String(cc || '').trim().toUpperCase();
                                  const loc = String(putAwayLocByContainer?.[child] || '').trim().toUpperCase();
                                  const checked = selectedSet.has(child);
                                  return (
                                    <div
                                      key={`ioc-normal-group-child-${parentCode}-${child || cidx}`}
                                      style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 8,
                                        border: '1px solid #d9d9d9',
                                        borderRadius: 6,
                                        padding: '4px 8px',
                                        background: '#fafafa',
                                      }}
                                    >
                                      <Checkbox
                                        checked={checked}
                                        disabled={!canOperate}
                                        onChange={(e) => {
                                          toggleIocNormalBundleSelection(child, !!e.target.checked);
                                          focusContainerInput();
                                        }}
                                      />
                                      <Tag style={{ marginInlineEnd: 0, borderColor: '#b7eb8f', background: '#f6ffed', color: '#237804' }}>子容器</Tag>
                                      <Typography.Text className="mono" style={{ maxWidth: 220 }} ellipsis copyable={{ text: child }}>
                                        {child || '-'}
                                      </Typography.Text>
                                      <span style={{ color: '#8c8c8c' }}>{loc || '-'}</span>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : null}

                  {standalone.length > 0 ? (
                    <div style={{ border: '1px solid #ffcf9f', borderRadius: 8, background: '#fff8f2', padding: 10 }}>
                      <div style={{ fontWeight: 600, color: '#9a5a00', marginBottom: 8 }}>独立容器</div>
                      <div className="wrapup-oc-grid">
                        {standalone.map((code) => {
                          const c = String(code || '').trim().toUpperCase();
                          const loc = String(putAwayLocByContainer?.[c] || '').trim().toUpperCase();
                          const done = !!loc;
                          const checked = selectedSet.has(c);
                          return (
                            <div key={`ioc-normal-standalone-${c}`} className={`wrapup-oc-item ${done ? 'done' : ''}`}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                                <Checkbox
                                  checked={checked}
                                  disabled={!canOperate}
                                  onChange={(e) => {
                                    toggleIocNormalBundleSelection(c, !!e.target.checked);
                                    focusContainerInput();
                                  }}
                                />
                                <Typography.Text className="mono" style={{ maxWidth: 220 }} ellipsis copyable={{ text: c }}>
                                  {c}
                                </Typography.Text>
                              </div>
                              <div className={`loc ${loc ? '' : 'empty'}`} style={{ flexShrink: 0, minWidth: 90, textAlign: 'right' }}>
                                {loc || '-'}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : null}
                </div>
              );
            };
            return (
              <div>
                <Tabs
                  size="small"
                  activeKey={putAwayActiveTab}
                  onChange={(k) => {
                    const key = String(k || '') as any;
                    setPutAwayActiveTab(key);
                    // 避免跨类混选：切换页签时清空选定与输入
                    setSelectedScanned([]);
                    setScanOutContainer('');
                    setScanLocation('');
                    focusContainerInput();
                  }}
                  items={[
                    {
                      key: 'ioc_normal',
                      label: `操作过程生成的容器IOC/SC(${iocNormal.length})`,
                      children: renderIocNormalCard(),
                    },
                    {
                      key: 'ioc_exc',
                      label: `无法操作产品生成的容器IOC(${iocException.length})`,
                      children: renderOneCard({ title: '无法操作产品生成的容器（异常IOC）', codes: iocException, selectable: true, doneCount: excDone, keyPrefix: 'ioc-exc' }),
                    },
                    {
                      key: 'sc',
                      label: `多拣货产品生成的容器SC(${scCodes.length})`,
                      children: renderOneCard({ title: '多拣货产品生成的容器（仓储容器）', codes: scCodes, selectable: true, doneCount: scDone, keyPrefix: 'sc' }),
                    },
                  ]}
                />
              </div>
            );
          })()}
        </Card>

        <Card size="small" title="步骤4: 备注与附件" headStyle={{ background:'#f6ffed', borderBottom: '1px solid #b7eb8f' }}>
          <div style={{ display:'flex', flexDirection:'column', gap: 14 }}>
            <div style={{ paddingBottom: 2 }}>
              <div style={{ marginBottom: 6 }}>
                <Text>收尾备注：</Text>
              </div>
              <Input.TextArea
                disabled={!canOperate}
                value={remarks}
                onChange={(e)=> setRemarks(e.target.value)}
                rows={3}
                placeholder="总结执行情况、待办、异常说明等"
              />
            </div>

            <Divider style={{ margin: '2px 0 10px' }} />

            <div>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom: 6 }}>
                <Text>附件（不限数量，支持表格/图片/视频/PDF/ZIP/RAR）：</Text>
                <Space size={8} style={{ display:'inline-flex', alignItems:'center' }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    已上传：<b>{(wrapupFileList || []).length}</b> 个
                  </Text>
                  <Button
                    size="small"
                    danger
                    disabled={!canOperate || isBatchMode || (wrapupFileList || []).length === 0}
                    onClick={() => {
                      if (isBatchMode) { message.warning('批次执行暂不支持附件'); return; }
                      Modal.confirm({
                        title: '确认删除全部附件？',
                        content: '该操作将删除当前步骤下已上传的全部附件（软删除），不可撤销。',
                        okText: '删除全部',
                        okButtonProps: { danger: true },
                        cancelText: '取消',
                        onOk: async () => {
                          const list = Array.isArray(wrapupFileList) ? wrapupFileList : [];
                          const ids = list
                            .map((f:any) => String(f?.response?.id || f?.uid || '').trim())
                            .filter((id:string) => !!id);
                          if (ids.length === 0) {
                            setWrapupFileList([]);
                            message.success('已清空附件列表');
                            return;
                          }
                          try {
                            // 批量删除：逐个调用 delete（后端为软删除）
                            const results = await Promise.allSettled(ids.map((id:string) =>
                              request.delete(`/api/v1/instruction/exec/step-attachments/${id}/`)
                            ));
                            const okCnt = results.filter(r => r.status === 'fulfilled').length;
                            const failIdx = results
                              .map((r, i) => ({ r, i }))
                              .filter(x => x.r.status === 'rejected')
                              .map(x => x.i);
                            if (failIdx.length === 0) {
                              setWrapupFileList([]);
                              message.success(`已删除全部附件（${okCnt}个）`);
                              return;
                            }
                            // 部分失败：保留失败项
                            const keepIdSet = new Set<string>(failIdx.map(i => ids[i]));
                            setWrapupFileList(list.filter((f:any) => {
                              const id = String(f?.response?.id || f?.uid || '').trim();
                              return keepIdSet.has(id);
                            }));
                            message.warning(`已删除 ${okCnt} 个附件，${failIdx.length} 个删除失败（已保留失败项）`);
                          } catch (e:any) {
                            message.error(e?.response?.data?.detail || e?.message || '批量删除失败');
                          }
                        }
                      });
                    }}
                  >
                    删除全部
                  </Button>
                </Space>
              </div>
              <Upload
                multiple
                disabled={!canOperate || isBatchMode}
                accept=".xls,.xlsx,.csv,.tsv,.png,.jpg,.jpeg,.webp,.gif,.bmp,.mp4,.mov,.avi,.mkv,.webm,.pdf,.zip,.rar"
                showUploadList={false}
                customRequest={async (opts: any) => {
                  const { file, onSuccess, onError } = opts || {};
                  try {
                    if (isBatchMode) throw new Error('批次执行暂不支持附件');
                    const sid = Number(stepId || 0);
                    if (!(sid > 0)) throw new Error('stepId 缺失，无法上传附件');
                    const fd = new FormData();
                    fd.append('step', String(sid));
                    fd.append('file', file);
                    const resp = await request.post('/api/v1/instruction/exec/step-attachments/', fd, {
                      headers: { 'Content-Type': 'multipart/form-data' } as any
                    });
                    const row = resp?.data || {};
                    const id = String(row?.id ?? '');
                    const url = String(row?.file || '').trim();
                    const name = String(row?.file_name || '').trim() || String((file as any)?.name || '') || (url ? url.split('/').pop() : '') || `附件#${id}`;
                    setWrapupFileList((prev: any[]) => {
                      const next = Array.isArray(prev) ? [...prev] : [];
                      // 去掉同名 uploading 占位
                      const cleaned = next.filter((f: any) => !(f && (f.status === 'uploading') && String(f.name || '') === String((file as any)?.name || '')));
                      cleaned.unshift({ uid: id || `${Date.now()}_${Math.random()}`, name, status: 'done', url, response: row });
                      return cleaned;
                    });
                    if (onSuccess) onSuccess(row);
                    message.success('附件上传成功');
                  } catch (e: any) {
                    const data = e?.response?.data;
                    if (onError) onError(e);
                    Modal.error({
                      title: '附件上传失败',
                      content: data?.detail || data?.message || e?.message || '上传失败',
                      okText: '知道了',
                    });
                  }
                }}
                onRemove={async (file: any) => {
                  try {
                    if (isBatchMode) { message.warning('批次执行暂不支持附件'); return false; }
                    const id = String(file?.response?.id || file?.uid || '').trim();
                    if (!id) return true;
                    await request.delete(`/api/v1/instruction/exec/step-attachments/${id}/`);
                    setWrapupFileList((prev: any[]) => (Array.isArray(prev) ? prev.filter((f:any) => String(f?.uid || '') !== String(file?.uid || '')) : []));
                    message.success('已删除附件');
                    return true;
                  } catch (e: any) {
                    message.error(e?.response?.data?.detail || e?.message || '删除失败');
                    return false;
                  }
                }}
                onPreview={async (file: any) => {
                  const url = String(file?.url || file?.thumbUrl || '').trim();
                  if (!url) return;
                  const name = String(file?.name || '附件预览');
                  const lower = name.toLowerCase();
                  const isImg = ['.png', '.jpg', '.jpeg', '.webp', '.gif', '.bmp'].some(ext => lower.endsWith(ext));
                  if (isImg) {
                    setWrapupPreviewTitle(name);
                    setWrapupPreviewUrl(url);
                    setWrapupPreviewOpen(true);
                    return;
                  }
                  try { window.open(url, '_blank'); } catch {}
                }}
              >
                <Button
                  disabled={!canOperate || isBatchMode}
                  style={{
                    background: '#e6f4ff',
                    borderColor: '#91caff',
                    color: '#0958d9',
                  }}
                >
                  上传附件
                </Button>
              </Upload>
              {isBatchMode ? (
                <div style={{ marginTop: 6, color: '#8c8c8c' }}>批次执行暂不支持附件</div>
              ) : null}
              {/* 附件列表（自渲染 + 分页），避免一次渲染过长 */}
              {(wrapupFileList || []).length > 0 ? (
                <div style={{ marginTop: 8 }}>
                  <Table
                    size="small"
                    rowKey={(r:any, idx?:number) => String(r?.uid || r?.response?.id || (idx ?? 0))}
                    pagination={false}
                    dataSource={(() => {
                      const list = Array.isArray(wrapupFileList) ? wrapupFileList : [];
                      const start = (wrapupAttPage - 1) * wrapupAttPageSize;
                      return list.slice(start, start + wrapupAttPageSize);
                    })()}
                    columns={[
                      {
                        title: '序号',
                        key: '__idx',
                        width: 70,
                        align: 'center' as any,
                        render: (_:any, __:any, idx:number) => ((wrapupAttPage - 1) * wrapupAttPageSize) + idx + 1
                      },
                      {
                        title: '文件名',
                        dataIndex: 'name',
                        key: 'name',
                        render: (v:any, r:any) => (
                          <Typography.Text
                            style={{ maxWidth: 420 }}
                            ellipsis
                            title={String(v || '')}
                          >
                            {String(v || '-') }
                          </Typography.Text>
                        )
                      },
                      {
                        title: '操作',
                        key: 'ops',
                        width: 180,
                        align: 'center' as any,
                        render: (_:any, r:any) => {
                          const url = String(r?.url || r?.thumbUrl || '').trim();
                          const canPreview = !!url;
                          return (
                            <Space size={6}>
                              <Button
                                size="small"
                                disabled={!canPreview}
                                onClick={async () => {
                                  if (!url) return;
                                  const name = String(r?.name || '附件预览');
                                  const lower = name.toLowerCase();
                                  const isImg = ['.png', '.jpg', '.jpeg', '.webp', '.gif', '.bmp'].some(ext => lower.endsWith(ext));
                                  if (isImg) {
                                    setWrapupPreviewTitle(name);
                                    setWrapupPreviewUrl(url);
                                    setWrapupPreviewOpen(true);
                                    return;
                                  }
                                  try { window.open(url, '_blank'); } catch {}
                                }}
                              >
                                查看
                              </Button>
                              <Button
                                size="small"
                                danger
                                disabled={!canOperate || isBatchMode}
                                style={{ borderColor: '#ff4d4f', color: '#ff4d4f' }}
                                onClick={async () => {
                                  try {
                                    if (isBatchMode) { message.warning('批次执行暂不支持附件'); return; }
                                    const id = String(r?.response?.id || r?.uid || '').trim();
                                    if (!id) return;
                                    await request.delete(`/api/v1/instruction/exec/step-attachments/${id}/`);
                                    setWrapupFileList((prev: any[]) => (Array.isArray(prev) ? prev.filter((f:any) => String(f?.uid || '') !== String(r?.uid || '')) : []));
                                    message.success('已删除附件');
                                  } catch (e:any) {
                                    message.error(e?.response?.data?.detail || e?.message || '删除失败');
                                  }
                                }}
                              >
                                删除
                              </Button>
                            </Space>
                          );
                        }
                      }
                    ] as any}
                    locale={{ emptyText: '无附件' }}
                  />
                  <div style={{ display:'flex', justifyContent:'flex-end', marginTop: 8 }}>
                    <Pagination
                      size="small"
                      current={wrapupAttPage}
                      pageSize={wrapupAttPageSize}
                      total={(wrapupFileList || []).length}
                      showSizeChanger
                      showQuickJumper
                      onChange={(p, ps) => { setWrapupAttPage(p); setWrapupAttPageSize(ps); }}
                      showTotal={(total) => `共 ${total} 条`}
                    />
                  </div>
                </div>
              ) : null}
              <div style={{ marginTop: 6, color:'#8c8c8c', fontSize: 12, lineHeight: 1.6 }}>
                - 表格：xls/xlsx/csv/tsv　- 图片：png/jpg/jpeg/webp/gif/bmp　- 视频：mp4/mov/avi/mkv/webm　- 文档：pdf　- 压缩：zip/rar
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* 分配明细弹窗 */}
      <Modal
        open={allocModalOpen}
        title={`来源IU分配明细 - ${allocModalTitle || ''}`}
        onCancel={() => setAllocModalOpen(false)}
        footer={[<Button key="close" onClick={() => setAllocModalOpen(false)}>关闭</Button>]}
        width={720}
      >
        <Table
          size="small"
          rowKey={(r:any) => String(r?.inventory_unit_id || '')}
          pagination={false}
          dataSource={allocModalRows as any}
          columns={[
            { title: '序号', key: '__idx', width: 70, align: 'center' as any, render: (_:any, __:any, idx:number) => idx + 1 },
            { title: '库存单元(IU)', dataIndex: 'unit_code', key: 'unit_code', render: (v:any) => (
              <Typography.Text copyable={{ text: String(v || '') }} style={{ fontFamily:'monospace', color:'#595959' }}>
                {String(v || '-')}
              </Typography.Text>
            )},
            { title: '数量', dataIndex: 'quantity', key: 'quantity', width: 120, align: 'right' as any, render: (v:any) => Number(v || 0) },
          ] as any}
          locale={{ emptyText: '无分配记录' }}
        />
      </Modal>

      {/* 步骤4：图片附件预览 */}
      <Modal
        open={wrapupPreviewOpen}
        title={wrapupPreviewTitle}
        onCancel={() => { setWrapupPreviewOpen(false); setWrapupPreviewUrl(''); }}
        footer={[<Button key="close" onClick={() => { setWrapupPreviewOpen(false); setWrapupPreviewUrl(''); }}>关闭</Button>]}
        width={860}
      >
        {wrapupPreviewUrl ? (
          <div style={{ textAlign:'center' }}>
            <img src={wrapupPreviewUrl} alt="preview" style={{ maxWidth:'100%', maxHeight: 560 }} />
          </div>
        ) : (
          <div style={{ color:'#999' }}>无预览</div>
        )}
      </Modal>

      {/* 步骤1说明 */}
      <Modal
        open={unoperableHelpOpen}
        title={isSortCountInstruction ? '分拣出的无法处理产品生成指令产出容器IOC - 说明' : '无法操作产品生成容器 - 说明'}
        onCancel={() => setUnoperableHelpOpen(false)}
        footer={[<Button key="close" onClick={() => setUnoperableHelpOpen(false)}>关闭</Button>]}
        width={860}
      >
        {isSortCountInstruction ? (
          <div style={{ lineHeight: 1.8, color: '#262626' }}>
            <div style={{ marginBottom: 8 }}>
              分拣清点指令将按照当前分拣需求进入专属处理逻辑，具体处理逻辑稍后实现。
            </div>
            <ul style={{ margin: '6px 0 0 18px' }}>
              <li><strong>分拣需求1:分拣混装入库LIU/BIU为分装入库分拣组</strong>：参考其他指令处理方式，按照分拣清点阶段2的装箱结果和拣货阶段拣货数量计算每种操作对象的 gap，再按输入的无法操作产品数量自动分配。</li>
              <li><strong>分拣需求2: 原箱入库的BIU/PIU分拣为散货库存单元LIU</strong>：收尾阶段直接将无法处理产品记录到 AIU 内容事实（用于异常承接），终审阶段再统一执行库存结算与核算；无法操作产品不必和指令操作对象一一匹配。</li>
              <li><strong>分拣需求3: 单款LIU按产品特性细分</strong>：收尾阶段直接将无法处理产品记录到 AIU 内容事实（用于异常承接），需填写数量与总重量；终审阶段再统一执行库存结算与核算。</li>
            </ul>
            <div style={{ marginTop: 8, color: '#8c8c8c' }}>
              统一口径说明：步骤1在收尾阶段仅记录来源 IU 归属事实与分配结果，不执行硬库存变更；终审提交时才会统一执行无法操作数量转换为 AIU 的库存落库动作。
            </div>
            <div style={{ marginTop: 12 }}>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>各指令模板执行分工（收尾 vs 终审）</div>
              <ul style={{ margin: '6px 0 0 18px' }}>
                <li><strong>换标装箱</strong>：收尾记录换标事实、IOC草稿/内容、尺寸重量和异常分配；终审统一固化 IOC/内容，并按异常事实执行 source IU -&gt; AIU 转换（如存在）。</li>
                <li><strong>一件代发</strong>：收尾记录拣货/装箱/发运与异常事实；终审按最终事实扣减源 IU、释放执行占用，异常数量统一转 AIU（如存在）。</li>
                <li><strong>常规出库</strong>：收尾记录出库明细、装箱与调整事实；终审按确认结果扣减源 IU、释放占用，异常数量统一转 AIU（如存在）。</li>
                <li><strong>分拣清点-需求1（need1）</strong>：收尾记录分拣/容器/无法操作事实；终审不做源 IU 扣减，执行目标数量修正、执行期锁定释放、分拣组调整与新 IU 初始库存/库龄入账。</li>
                <li><strong>分拣清点-需求2（need2）</strong>：收尾记录 AIU 异常承接事实（不做硬库存）；终审统一执行库存结算、释放占用、分拣组调整与新 IU 初始库存/库龄入账。</li>
                <li><strong>分拣清点-需求3（need3）</strong>：收尾记录 AIU 异常承接事实（需填写数量与总重量，不做硬库存）；终审统一执行库存结算、释放占用、分拣组调整与新 IU 初始库存/库龄入账。</li>
                <li><strong>批量处理</strong>：收尾只记录批次状态与待终审队列；终审逐指令执行各自库存提交（每单独立幂等与事务），批次层不直接写库存账。</li>
              </ul>
            </div>
          </div>
        ) : (
          <div style={{ lineHeight: 1.8, color: '#262626' }}>
            <div style={{ marginBottom: 8 }}>
              本板块用于处理现场出现的“无法操作/破损/无法继续换标装箱”的产品：系统会把它们放入一个<strong>指令产出异常内容容器</strong>中，并为后续销毁扣减库存提供 IU 归属。
            </div>
            <div style={{ marginBottom: 8 }}>
              分配逻辑（理论值）：
              <ul style={{ margin: '6px 0 0 18px' }}>
                <li><strong>已拣未操作数量可用值</strong> = 拣货实际拣出数量 − 已操作数量（已操作数量包含：正常装箱内容 + 调整池）</li>
                <li>系统按上述“已拣未操作数量可用值”从大到小为库存单元（IU）分配无法操作数量</li>
                <li>若无法操作数量 &gt; 可用值总和，则需要你在弹窗中手动选择 IU 并分配剩余数量</li>
              </ul>
            </div>
            <div style={{ color: '#8c8c8c' }}>
              注：分配结果为系统推算，非确定值；生成的异常容器箱标可在列表右侧“打印箱标”再次预览/打印。
            </div>
            <div style={{ marginTop: 8, color: '#8c8c8c' }}>
              统一口径说明：步骤1在收尾阶段仅记录来源 IU 归属事实与分配结果，不执行硬库存变更；终审提交时才会统一执行无法操作数量转换为 AIU 的库存落库动作。
            </div>
            <div style={{ marginTop: 12 }}>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>各指令模板执行分工（收尾 vs 终审）</div>
              <ul style={{ margin: '6px 0 0 18px' }}>
                <li><strong>换标装箱</strong>：收尾记录换标事实、IOC草稿/内容、尺寸重量和异常分配；终审统一固化 IOC/内容，并按异常事实执行 source IU -&gt; AIU 转换（如存在）。</li>
                <li><strong>一件代发</strong>：收尾记录拣货/装箱/发运与异常事实；终审按最终事实扣减源 IU、释放执行占用，异常数量统一转 AIU（如存在）。</li>
                <li><strong>常规出库</strong>：收尾记录出库明细、装箱与调整事实；终审按确认结果扣减源 IU、释放占用，异常数量统一转 AIU（如存在）。</li>
                <li><strong>分拣清点-需求1（need1）</strong>：收尾记录分拣/容器/无法操作事实；终审不做源 IU 扣减，执行目标数量修正、执行期锁定释放、分拣组调整与新 IU 初始库存/库龄入账。</li>
                <li><strong>分拣清点-需求2（need2）</strong>：收尾记录 AIU 异常承接事实（不做硬库存）；终审统一执行库存结算、释放占用、分拣组调整与新 IU 初始库存/库龄入账。</li>
                <li><strong>分拣清点-需求3（need3）</strong>：收尾记录 AIU 异常承接事实（需填写数量与总重量，不做硬库存）；终审统一执行库存结算、释放占用、分拣组调整与新 IU 初始库存/库龄入账。</li>
                <li><strong>批量处理</strong>：收尾只记录批次状态与待终审队列；终审逐指令执行各自库存提交（每单独立幂等与事务），批次层不直接写库存账。</li>
              </ul>
            </div>
          </div>
        )}
      </Modal>

      {/* 超过总gap：手动分配 IU */}
      <Modal
        open={manualModalOpen}
        title="无法操作数量>已拣未操作数量可用值:请手动选择库存单元并分配数量"
        onCancel={() => setManualModalOpen(false)}
        okText="确认分配并生成容器"
        cancelText="取消"
        onOk={() => {
          const remain = Number(manualRemainder || 0);
          const entries = Object.entries(manualInputs || {}).map(([k, v]) => ({ inventory_unit_id: Number(k), quantity: Number(v || 0) }));
          const filtered = entries.filter(x => Number.isFinite(x.inventory_unit_id) && x.inventory_unit_id > 0 && Number.isFinite(x.quantity) && x.quantity > 0);
          const sum = filtered.reduce((s, x) => s + x.quantity, 0);
          if (sum !== remain) {
            message.error(`手动分配数量合计必须等于剩余数量：${remain}`);
            return;
          }
          setManualModalOpen(false);
          createExceptionContainer({ manual_allocations: filtered });
        }}
        width={860}
      >
        <div style={{ marginBottom: 10, color:'#a8071a', display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', gap: 8 }}>
          <div>
            剩余需手动分配数量：<b>{manualRemainder}</b>
          </div>
          <Input
            allowClear
            value={manualSearchKeyword}
            onChange={(e) => setManualSearchKeyword(String(e?.target?.value || ''))}
            placeholder="搜索库存单元系统编码或库存单元内容"
            style={{ width: 360 }}
          />
          <div />
        </div>
        <Table
          size="small"
          rowKey={(r:any) => String(r?.inventory_unit_id || '')}
          pagination={false}
          dataSource={filteredManualCandidates as any}
          columns={[
            { title: '序号', key: '__idx', width: 70, align: 'center' as any, render: (_:any, __:any, idx:number) => idx + 1 },
            { title: '库存单元(IU)', dataIndex: 'unit_code', key: 'unit_code', render: (v:any) => (
              <Typography.Text copyable={{ text: String(v || '') }} style={{ fontFamily:'monospace', color:'#595959' }}>
                {String(v || '-')}
              </Typography.Text>
            )},
            { title: '库存单元内容', key: 'content_lines', render: (_:any, r:any) => {
              const lines = Array.isArray(r?.content_lines) ? r.content_lines : [];
              if (!lines.length) return '-';
              return (
                <span style={{ display: 'inline-block', lineHeight: 1.5 }}>
                  {lines.map((t:any, i:number) => (
                    <div key={`iu-content-${String(r?.inventory_unit_id || '-')}-${i}`}>{String(t || '-')}</div>
                  ))}
                </span>
              );
            }},
            { title: '已拣未操作数量可用值', dataIndex: 'gap', key: 'gap', width: 180, align: 'right' as any, render: (v:any) => Number(v || 0) },
            { title: '手动分配数量', key: 'alloc', width: 180, align: 'right' as any, render: (_:any, r:any) => {
              const iuId = Number(r?.inventory_unit_id || 0);
              const key = String(iuId);
              const cur = Number((manualInputs || {})[key] || 0);
              return (
                <InputNumber
                  min={0}
                  precision={0}
                  value={cur}
                  onChange={(v)=> {
                    const nv = Number(v || 0);
                    setManualInputs(prev => ({ ...(prev || {}), [key]: nv }));
                  }}
                  style={{ width: 140 }}
                />
              );
            }},
          ] as any}
          locale={{ emptyText: '无候选IU' }}
        />
      </Modal>

      {/* 箱标预览（复用换标+装箱的 IOC 箱标样式） */}
      <Modal
        open={labelPreviewOpen}
        title={labelPreviewTitle || '预览'}
        onCancel={() => {
          setLabelPreviewOpen(false);
          try { if (labelPreviewUrl) URL.revokeObjectURL(labelPreviewUrl); } catch {}
          setLabelPreviewUrl('');
        }}
        footer={[
          <Button
            key="print"
            type="primary"
            disabled={!labelPreviewUrl}
            onClick={() => {
              if (!labelPreviewUrl) return;
              try {
                // 使用隐藏 iframe 触发打印，避免弹新窗口
                const iframe = document.createElement('iframe');
                iframe.style.position = 'fixed';
                iframe.style.right = '0';
                iframe.style.bottom = '0';
                iframe.style.width = '0';
                iframe.style.height = '0';
                iframe.style.border = '0';
                iframe.src = labelPreviewUrl;
                document.body.appendChild(iframe);
                iframe.onload = () => {
                  try {
                    iframe.contentWindow?.focus();
                    iframe.contentWindow?.print();
                  } catch {}
                  // 延迟清理，避免部分浏览器打印对话框打开前 iframe 被移除
                  setTimeout(() => {
                    try { document.body.removeChild(iframe); } catch {}
                  }, 1500);
                };
              } catch {}
            }}
          >
            打印
          </Button>,
          <Button key="close" onClick={() => {
            setLabelPreviewOpen(false);
            try { if (labelPreviewUrl) URL.revokeObjectURL(labelPreviewUrl); } catch {}
            setLabelPreviewUrl('');
          }}>关闭</Button>
        ]}
        width={820}
      >
        {labelPreviewLoading ? (
          <div style={{ color:'#999' }}>生成中...</div>
        ) : (
          labelPreviewUrl ? (
            <iframe
              title="output-container-label-preview"
              src={labelPreviewUrl}
              style={{ width: '100%', height: 620, border: '1px solid #f0f0f0', borderRadius: 6 }}
            />
          ) : (
            <div style={{ color:'#999' }}>无预览内容</div>
          )
        )}
      </Modal>

      {/* 步骤2：生成仓储容器前选择容器类型 */}
      <Modal
        open={overPickCreateOpen}
        title="选择容器类型"
        okText="确认生成"
        cancelText="取消"
        onCancel={() => {
          setOverPickCreateOpen(false);
          setOverPickCreateGroupIndex(null);
          focusOverPickOldCode();
        }}
        onOk={async () => {
          const gi = Number(overPickCreateGroupIndex ?? -1);
          if (gi < 0) { setOverPickCreateOpen(false); return; }
          setOverPickCreateOpen(false);
          await createStorageContainerForOverPickGroup(gi, overPickContainerType);
          setOverPickCreateGroupIndex(null);
        }}
      >
        <div style={{ display:'flex', alignItems:'center', gap: 10 }}>
          <span style={{ width: 90, color:'#595959' }}>容器类型</span>
          <Select
            value={overPickContainerType}
            loading={overPickContainerTypeLoading}
            style={{ width: 260 }}
            options={(overPickContainerTypeChoices || []).map(x => ({ value: x.value, label: x.label }))}
            onChange={(v)=> setOverPickContainerType(String(v || 'box'))}
          />
        </div>
        <div style={{ marginTop: 10, color:'#999' }}>
          默认选择“箱子”。确认后将创建仓储容器，并自动预览与打印标签。
        </div>
      </Modal>

      <Space style={{ marginTop: 10 }}>
        <Button onClick={doSaveDraft} loading={saving} disabled={!canOperate}>保存草稿</Button>
      </Space>
    </div>
  );
};

export default SubmitWrapUpRenderer;


